import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  extractMatchSchedulePayload,
  fetchLocalStreamPreview,
  fetchMatchLiveStatus,
  fetchMatchSchedule,
  fetchMatchStreamDetails,
  fetchRelayStatus,
  generateTeamJoinLink,
  generateMatchYouTubeStream,
  saveManualMatchSchedule,
  startRelay,
  stopRelay,
} from '../../../services/matchService';
import { fetchTournamentRegisteredPlayers, uploadMatchTeamLogo } from '../../../services/tournamentService';
import { getCommunityId } from '../../../utils/community';
import { getClubId } from '../../../utils/club';
import { normalizeId } from '../../../utils/ids';
import { getManualTeamPlayerOptions, getPlayerId } from '../../../utils/manualMatchPlayerOptions';
import { getMatchStartResumePath, getStartResumeLabel } from '../../../utils/matchStatus';
import { normalizeMatchConfig } from '../../../utils/normalizeMatchConfig';
import { buildUrl } from '../../../services/apiClient';
import {
  buildMatchManagerMatchesPath,
  buildMatchManagerMatchStartPath,
  buildMatchManagerMatchTossPath,
} from '../../../utils/matchManagerPaths';


const normalizeTournamentRoleKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[\s-]+/g, '_')
  .replace(/[^a-z0-9_]/g, '')
  .replace(/_+/g, '_')
  .replace(/^_|_$/g, '');

const getTournamentRoleKeys = (player = {}) => {
  const sources = [
    player?.tournamentRoles,
    player?.registeredPlayer?.tournamentRoles,
    player?.registration?.tournamentRoles,
    player?.player?.tournamentRoles,
    player?.originalPlayer?.tournamentRoles,
  ];
  const roles = [];
  let hasTournamentRoles = false;
  sources.forEach((source) => {
    if (Array.isArray(source)) {
      hasTournamentRoles = true;
      source.forEach((role) => {
        const normalized = normalizeTournamentRoleKey(role);
        if (normalized) roles.push(normalized);
      });
    }
  });
  return hasTournamentRoles ? Array.from(new Set(roles)) : ['player'];
};

const hasTournamentRole = (player, role) => getTournamentRoleKeys(player).includes(normalizeTournamentRoleKey(role));

const STREAMING_MODES = {
  DIRECT_MOBILE: 'DIRECT_MOBILE_TO_YOUTUBE',
  OBS: 'OBS_TO_YOUTUBE',
  LOCAL_MAC_TESTING: 'LOCAL_MAC_TEST',
};

const STREAM_PROVIDERS = {
  YOUTUBE: 'youtube',
  FACEBOOK: 'facebook',
};

const DEFAULT_OBS_CAMERA_COUNT = 3;

const LIVE_HEALTH_BADGE = {
  live: { label: 'Live', className: 'bg-success-subtle text-success' },
  connecting: { label: 'Connecting', className: 'bg-warning-subtle text-warning-emphasis' },
  offline: { label: 'Offline', className: 'bg-danger-subtle text-danger' },
  stream_lost: { label: 'Stream lost', className: 'bg-danger-subtle text-danger' },
};

const resolveLiveHealth = (liveStatus = {}) => {
  const streamHealth = String(liveStatus?.streamHealth || '').trim().toLowerCase();
  if (streamHealth && LIVE_HEALTH_BADGE[streamHealth]) return streamHealth;

  const streamStatus = String(liveStatus?.streamStatus || '').trim().toLowerCase();
  if (streamStatus === 'active') return 'live';
  if (liveStatus?.isLive) return 'stream_lost';
  return 'offline';
};

const normalizeStreamingMode = (mode) => {
  const normalized = typeof mode === 'string' ? mode.trim().toUpperCase() : '';
  if (normalized === STREAMING_MODES.OBS) return STREAMING_MODES.OBS;
  if (normalized === STREAMING_MODES.LOCAL_MAC_TESTING) return STREAMING_MODES.LOCAL_MAC_TESTING;
  return STREAMING_MODES.DIRECT_MOBILE;
};

const normalizeStreamProvider = (provider) => {
  const normalized = typeof provider === 'string' ? provider.trim().toLowerCase() : '';
  if (normalized === STREAM_PROVIDERS.FACEBOOK) return STREAM_PROVIDERS.FACEBOOK;
  return STREAM_PROVIDERS.YOUTUBE;
};

const normalizeCameraCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_OBS_CAMERA_COUNT;
  return Math.min(8, Math.max(1, Math.floor(parsed)));
};

const normalizeJoinStatus = (status) => {
  const value = String(status || '').trim().toLowerCase();
  if (value === 'substitute') return 'substitute';
  if (value === 'unannounced') return 'unannounced';
  return 'playing';
};

const buildObsCameras = ({ matchIdentifier = '', ingestUrl = '', cameraCount = DEFAULT_OBS_CAMERA_COUNT, cameras = [] } = {}) => {
  const count = normalizeCameraCount(cameraCount);
  const safeIngestUrl = String(ingestUrl || '').trim();
  const keySeed = String(matchIdentifier || 'match').trim().replace(/[^a-zA-Z0-9_-]+/g, '_').toLowerCase() || 'match';
  return Array.from({ length: count }).map((_, index) => {
    const existingCamera = Array.isArray(cameras) ? cameras[index] || {} : {};
    const streamKey = String(existingCamera.streamKey || `${keySeed}_cam${index + 1}`).trim() || `${keySeed}_cam${index + 1}`;
    const cameraIngest = String(existingCamera.ingestUrl || safeIngestUrl).trim();
    const publishUrl = String(existingCamera.publishUrl || (cameraIngest && streamKey ? `${cameraIngest}/${streamKey}` : '')).trim();
    return {
      id: existingCamera.id || `cam${index + 1}`,
      label: existingCamera.label || `Camera ${index + 1}`,
      ingestUrl: cameraIngest,
      streamKey,
      publishUrl,
    };
  });
};

const createEmptyConfiguration = () => ({
  venue: '',
  notes: '',
  scorer: '',
  umpire1: '',
  umpire2: '',
  matchReferee: '',
  streamUrl: '',
  rtmpStreamKey: '',
  youtubeStreamKey: '',
  youtubeIngestUrl: 'rtmp://a.rtmp.youtube.com/live2',
  rtmpIngestUrl: '',
  streamProvider: 'youtube',
  streamingMode: STREAMING_MODES.DIRECT_MOBILE,
  streamingConfig: {
    mode: STREAMING_MODES.DIRECT_MOBILE,
    overlayMode: 'burn_in',
    youtube: {
      ingestUrl: 'rtmp://a.rtmp.youtube.com/live2',
      streamKey: '',
      playbackUrl: '',
      videoId: '',
    },
    local: {
      ingestUrl: 'rtmp://192.168.29.26:1935/stream',
      streamKey: '',
    },
    obs: {
      ingestUrl: 'rtmp://192.168.29.26:1935/stream',
      streamKey: '',
    },
    obsConfig: {
      cameraCount: DEFAULT_OBS_CAMERA_COUNT,
      ingestUrl: 'rtmp://192.168.29.26:1935/stream',
      cameras: buildObsCameras({
        ingestUrl: 'rtmp://192.168.29.26:1935/stream',
        cameraCount: DEFAULT_OBS_CAMERA_COUNT,
      }),
    },
  },
  livePlaybackUrl: '',
  youtubeVideoId: '',
  overlayMode: 'burn_in',
  overlayWebUrl: '',
  overlayAuthToken: '',
  tossWinner: '',
  tossDecision: '',
  overs: '',
  maxOversPerPlayer: '',
  maxPlayersAtMaxOvers: '',
  powerPlayStartOver: '',
  powerPlayEndOver: '',
  playersPerSide: '',
  playingCount: '',
  squadSize: '',
  allowLastBatsman: false,
  team1SquadPlayers: [],
  team2SquadPlayers: [],
  team1PlayingPlayers: [],
  team2PlayingPlayers: [],
  team1SubstitutePlayers: [],
  team2SubstitutePlayers: [],
  team1UnannouncedPlayers: [],
  team2UnannouncedPlayers: [],
  team1Players: [],
  team2Players: [],
  team1Captain: '',
  team1ViceCaptain: '',
  team1WicketKeeper: '',
  team2Captain: '',
  team2ViceCaptain: '',
  team2WicketKeeper: '',
  playerJoinLinks: {},
});

const uniquePlayers = (players = []) => [...new Set((Array.isArray(players) ? players : []).filter(Boolean))];

export const getDefaultStatusForNewPlayer = () => 'unannounced';

export const applyDefaultPlayerStatuses = (players = [], playingPlayersPerSide = 0, squadSize = 0) => {
  const result = [];

  uniquePlayers(players).forEach((player) => {
    const normalizedPlayer = typeof player === 'string' ? { playerId: player } : { ...player };
    const status = ['playing', 'substitute', 'unannounced'].includes(normalizedPlayer.status)
      ? normalizedPlayer.status
      : getDefaultStatusForNewPlayer(result, playingPlayersPerSide, squadSize);
    result.push({ ...normalizedPlayer, status });
  });

  return result;
};

const deriveManualTeamStatus = ({
  squadPlayers = [],
  playingPlayers = [],
  substitutePlayers = [],
  unannouncedPlayers = [],
  playingCount = 0,
  squadSize = 0,
}) => {
  const squad = uniquePlayers(squadPlayers);
  const squadSet = new Set(squad);
  const playing = uniquePlayers(playingPlayers).filter((id) => squadSet.has(id));
  const playingSet = new Set(playing);
  const substitutes = uniquePlayers(substitutePlayers).filter((id) => squadSet.has(id) && !playingSet.has(id));
  const substituteSet = new Set(substitutes);
  const unannounced = uniquePlayers(unannouncedPlayers)
    .filter((id) => squadSet.has(id) && !playingSet.has(id) && !substituteSet.has(id));
  const assignedSet = new Set([...playing, ...substitutes, ...unannounced]);
  const unassigned = squad.filter((id) => !assignedSet.has(id));

  unassigned.forEach((playerId) => {
    const status = getDefaultStatusForNewPlayer(squad.slice(0, squad.indexOf(playerId)), playingCount, squadSize);
    if (status === 'playing') playing.push(playerId);
    else if (status === 'substitute') substitutes.push(playerId);
    else unannounced.push(playerId);
  });

  return { squad, playing, substitutes, unannounced, announced: [...playing, ...substitutes] };
};

const getTeamStatusKeys = (playingKey) => ({
  substituteKey: playingKey === 'team1PlayingPlayers' ? 'team1SubstitutePlayers' : 'team2SubstitutePlayers',
  unannouncedKey: playingKey === 'team1PlayingPlayers' ? 'team1UnannouncedPlayers' : 'team2UnannouncedPlayers',
  legacyKey: playingKey === 'team1PlayingPlayers' ? 'team1Players' : 'team2Players',
});

const updateTeamSelection = ({ configuration, squadKey, playingKey, nextSquad }) => {
  const { substituteKey, unannouncedKey, legacyKey } = getTeamStatusKeys(playingKey);
  const currentSquad = uniquePlayers(configuration[squadKey]);
  const playingCount = Number(configuration.playingCount ?? configuration.playersPerSide) || 0;
  const squadSize = Number(configuration.squadSize) || 0;
  const allowedSquad = uniquePlayers(nextSquad);
  const allowedSet = new Set(allowedSquad);
  const playing = uniquePlayers(configuration[playingKey]).filter((id) => allowedSet.has(id));
  const substitutes = uniquePlayers(configuration[substituteKey]).filter((id) => allowedSet.has(id));
  const unannounced = uniquePlayers(configuration[unannouncedKey]).filter((id) => allowedSet.has(id));
  const assigned = new Set([...playing, ...substitutes, ...unannounced]);

  allowedSquad.forEach((playerId) => {
    if (assigned.has(playerId) || currentSquad.includes(playerId)) return;
    const status = getDefaultStatusForNewPlayer(currentSquad.filter((id) => allowedSet.has(id)), playingCount, squadSize);
    if (status === 'playing') playing.push(playerId);
    else if (status === 'substitute') substitutes.push(playerId);
    else unannounced.push(playerId);
    currentSquad.push(playerId);
    assigned.add(playerId);
  });

  return {
    ...configuration,
    [squadKey]: allowedSquad,
    [playingKey]: playing,
    [substituteKey]: substitutes,
    [unannouncedKey]: unannounced,
    [legacyKey]: allowedSquad,
  };
};

const createEmptyMatch = (matchNumber = 1, day = 1) => ({
  localId: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  id: '',
  day,
  dayLabel: '',
  date: '',
  time: '',
  matchNumber,
  team1: '',
  team2: '',
  team1Logo: '',
  team2Logo: '',
  configuration: createEmptyConfiguration(),
});

const resolveYouTubePublicStreamUrl = (youtubeStreamKey = '', rtmpStreamKey = '') => {
  const preferredValue =
    typeof youtubeStreamKey === 'string' && youtubeStreamKey.trim()
      ? youtubeStreamKey
      : rtmpStreamKey;
  const rawValue = typeof preferredValue === 'string' ? preferredValue.trim() : '';
  if (!rawValue) return '';

  if (/^https?:\/\//i.test(rawValue)) return rawValue;

  const sanitizedKey = rawValue
    .replace(/^.*live\//i, '')
    .replace(/^.*watch\?v=/i, '')
    .replace(/^.*youtu\.be\//i, '')
    .replace(/[?&].*$/, '')
    .trim();

  if (!sanitizedKey) return '';
  return `https://www.youtube.com/live/${sanitizedKey}`;
};

const resolveYouTubeLiveLinkFromResponse = (payload = {}) => {
  const candidates = [
    payload?.youtubeLiveLink,
    payload?.youtubeUrl,
    payload?.liveUrl,
    payload?.playbackUrl,
    payload?.streamUrl,
    payload?.livePlaybackUrl,
    payload?.streamingConfig?.youtube?.playbackUrl,
    payload?.streamingConfig?.youtube?.youtubeUrl,
    payload?.configuration?.youtubeLiveLink,
    payload?.configuration?.streamUrl,
    payload?.configuration?.livePlaybackUrl,
  ];

  const directUrl = candidates.find((value) => typeof value === 'string' && /^https?:\/\//i.test(value.trim()));
  if (directUrl) return directUrl.trim();

  const videoId = [payload?.videoId, payload?.youtubeVideoId, payload?.streamingConfig?.youtube?.videoId]
    .find((value) => typeof value === 'string' && value.trim());
  if (videoId) return `https://www.youtube.com/watch?v=${videoId.trim()}`;

  return '';
};

const resolveLogoUrl = (logo) => {
  const value = String(logo || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  return buildUrl(value);
};

const normalizeIdList = (value) => (Array.isArray(value) ? value.map((item) => normalizeId(item)).filter(Boolean) : []);

const normalizeMaybeId = (value) => normalizeId(value) || (typeof value === 'string' ? value : '');

const isObjectRecord = (value) => value && typeof value === 'object' && !Array.isArray(value);

const normalizeRouteId = (value) => {
  const normalized = normalizeId(value);

  return normalized === '[object Object]' ? '' : normalized;
};

const resolveMatchConfiguration = (match = {}) => {
  const candidates = [
    match?.matchConfiguration?.configuration,
    match?.manualSettings?.configuration,
    match?.settings?.configuration,
    match?.format?.configuration,
    match?.config,
    match?.matchConfiguration,
    match?.manualSettings,
    match?.settings,
    match?.format,
    match?.configuration,
  ];

  return candidates
    .filter(isObjectRecord)
    .reduce((configuration, candidate) => ({ ...configuration, ...candidate }), {});
};

const normalizeMatch = (match, index) => {
  const matchId = match?.id || match?._id || '';
  const config = resolveMatchConfiguration(match);
  const mode = normalizeStreamingMode(config.streamingConfig?.mode || config.streamingMode);
  const provider = normalizeStreamProvider(config.streamProvider || config.streamingConfig?.provider);
  const youtubeIngestUrl = config?.streamingConfig?.youtube?.ingestUrl || config.youtubeIngestUrl || 'rtmp://a.rtmp.youtube.com/live2';
  const youtubeStreamKey = config?.streamingConfig?.youtube?.streamKey || config.youtubeStreamKey || config.rtmpStreamKey || '';
  const playbackUrl = config?.streamingConfig?.youtube?.playbackUrl || config.youtubePlaybackUrl || config.youtubeLiveLink || config.livePlaybackUrl || config.streamUrl || '';
  const obsIngestUrl = config?.streamingConfig?.obs?.ingestUrl || config.rtmpIngestUrl || 'rtmp://192.168.29.26:1935/stream';
  const localIngestUrl = config?.streamingConfig?.local?.ingestUrl || config.rtmpIngestUrl || 'rtmp://192.168.29.26:1935/stream';
  const rtmpStreamKey = config?.streamingConfig?.obs?.streamKey || config?.streamingConfig?.local?.streamKey || config.rtmpStreamKey || '';
  const overlayMode = config?.streamingConfig?.overlayMode || config?.overlayMode || 'burn_in';
  const obsCameraCount = normalizeCameraCount(config?.streamingConfig?.obsConfig?.cameraCount);
  const obsCameras = buildObsCameras({
    matchIdentifier: matchId || `match-${index + 1}`,
    ingestUrl: config?.streamingConfig?.obsConfig?.ingestUrl || obsIngestUrl,
    cameraCount: obsCameraCount,
    cameras: config?.streamingConfig?.obsConfig?.cameras,
  });

  return {
    localId: matchId || `match-${index + 1}`,
    id: matchId,
    day: match?.day || index + 1,
    dayLabel: match?.dayLabel || '',
    date: match?.date || '',
    time: match?.time || '',
    matchNumber: match?.matchNumber || index + 1,
    team1: match?.team1 || '',
    team2: match?.team2 || '',
    team1Logo: match?.team1Logo || match?.configuration?.team1Logo || match?.config?.team1Logo || '',
    team2Logo: match?.team2Logo || match?.configuration?.team2Logo || match?.config?.team2Logo || '',
    configuration: {
      ...createEmptyConfiguration(),
      ...config,
      streamProvider: provider,
      streamingMode: mode,
      youtubeIngestUrl,
      youtubeStreamKey,
      livePlaybackUrl: playbackUrl,
      streamUrl: config.streamUrl || playbackUrl,
      rtmpIngestUrl: mode === STREAMING_MODES.LOCAL_MAC_TESTING ? localIngestUrl : obsIngestUrl,
      rtmpStreamKey,
      streamingConfig: {
        ...createEmptyConfiguration().streamingConfig,
        ...(config?.streamingConfig || {}),
        mode,
        overlayMode,
        youtube: {
          ...createEmptyConfiguration().streamingConfig.youtube,
          ...(config?.streamingConfig?.youtube || {}),
          ingestUrl: youtubeIngestUrl,
          streamKey: youtubeStreamKey,
          playbackUrl,
          videoId: config?.streamingConfig?.youtube?.videoId || config.youtubeVideoId || '',
        },
        local: {
          ...createEmptyConfiguration().streamingConfig.local,
          ...(config?.streamingConfig?.local || {}),
          ingestUrl: localIngestUrl,
          streamKey: config?.streamingConfig?.local?.streamKey || config.rtmpStreamKey || '',
        },
        obs: {
          ...createEmptyConfiguration().streamingConfig.obs,
          ...(config?.streamingConfig?.obs || {}),
          ingestUrl: obsIngestUrl,
          streamKey: config?.streamingConfig?.obs?.streamKey || config.rtmpStreamKey || '',
          playbackUrl: config?.streamingConfig?.obs?.playbackUrl || playbackUrl,
        },
        obsConfig: {
          cameraCount: obsCameraCount,
          ingestUrl: config?.streamingConfig?.obsConfig?.ingestUrl || obsIngestUrl,
          cameras: obsCameras,
        },
      },
      overlayMode,
      overs: config?.overs ?? '',
      maxOversPerPlayer: config?.maxOversPerPlayer ?? '',
      maxPlayersAtMaxOvers: config?.maxPlayersAtMaxOvers ?? '',
      powerPlayStartOver: config?.powerPlayStartOver ?? '',
      powerPlayEndOver: config?.powerPlayEndOver ?? '',
      playersPerSide: config?.playersPerSide ?? config?.playingCount ?? '',
      playingCount: config?.playingCount ?? config?.playersPerSide ?? '',
      squadSize: config?.squadSize ?? config?.playersPerSide ?? '',
      allowLastBatsman: Boolean(config?.allowLastBatsman),
      team1SquadPlayers: normalizeIdList(config?.team1SquadPlayers).length
        ? normalizeIdList(config.team1SquadPlayers)
        : (normalizeIdList(config?.team1PlayingPlayers).length ? normalizeIdList(config.team1PlayingPlayers) : normalizeIdList(config?.team1Players)),
      team2SquadPlayers: normalizeIdList(config?.team2SquadPlayers).length
        ? normalizeIdList(config.team2SquadPlayers)
        : (normalizeIdList(config?.team2PlayingPlayers).length ? normalizeIdList(config.team2PlayingPlayers) : normalizeIdList(config?.team2Players)),
      team1PlayingPlayers: normalizeIdList(config?.team1PlayingPlayers).length
        ? normalizeIdList(config.team1PlayingPlayers)
        : normalizeIdList(config?.team1Players),
      team2PlayingPlayers: normalizeIdList(config?.team2PlayingPlayers).length
        ? normalizeIdList(config.team2PlayingPlayers)
        : normalizeIdList(config?.team2Players),
      team1SubstitutePlayers: normalizeIdList(config?.team1SubstitutePlayers),
      team2SubstitutePlayers: normalizeIdList(config?.team2SubstitutePlayers),
      team1UnannouncedPlayers: normalizeIdList(config?.team1UnannouncedPlayers),
      team2UnannouncedPlayers: normalizeIdList(config?.team2UnannouncedPlayers),
      team1Players: normalizeIdList(config?.team1Players),
      team2Players: normalizeIdList(config?.team2Players),
      team1Captain: normalizeMaybeId(config?.team1Captain),
      team1ViceCaptain: normalizeMaybeId(config?.team1ViceCaptain),
      team1WicketKeeper: normalizeMaybeId(config?.team1WicketKeeper),
      team2Captain: normalizeMaybeId(config?.team2Captain),
      team2ViceCaptain: normalizeMaybeId(config?.team2ViceCaptain),
      team2WicketKeeper: normalizeMaybeId(config?.team2WicketKeeper),
    },
  };
};

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const MatchManagerManualFormatPage = () => {
  const { token, user } = useAuth();
  const location = useLocation();
  const {
    organizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    individualTournamentId: routeIndividualTournamentId,
    matchManagerId: routeMatchManagerId,
    managerId: routeManagerId,
    tournamentId,
  } = useParams();

  const communityId = useMemo(() => getCommunityId(routeCommunityId), [routeCommunityId]);
  const clubId = useMemo(() => getClubId(routeClubId), [routeClubId]);
  const managerId = normalizeRouteId(routeMatchManagerId || routeManagerId) || normalizeRouteId(user);
  const isIndividualTournamentRoute = location.pathname.startsWith('/individual-tournament/');
  const individualTournamentId = isIndividualTournamentRoute
    ? normalizeRouteId(routeIndividualTournamentId || tournamentId)
    : '';

  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([createEmptyMatch()]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [copyStatus, setCopyStatus] = useState({});
  const [registeredPlayers, setRegisteredPlayers] = useState([]);
  const [playersError, setPlayersError] = useState('');
  const [isPlayersLoading, setIsPlayersLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [logoUploadState, setLogoUploadState] = useState({});
  const [streamDetailsByMatch, setStreamDetailsByMatch] = useState({});
  const [relayStatusByMatch, setRelayStatusByMatch] = useState({});
  const [liveStatusByMatch, setLiveStatusByMatch] = useState({});
  const [liveStatusErrorByMatch, setLiveStatusErrorByMatch] = useState({});
  const [streamUrlStatusByMatch, setStreamUrlStatusByMatch] = useState({});
  const [isGeneratingYouTubeByMatch, setIsGeneratingYouTubeByMatch] = useState({});
  const [localPreviewEnabledByMatch, setLocalPreviewEnabledByMatch] = useState({});
  const [localPreviewStatusByMatch, setLocalPreviewStatusByMatch] = useState({});
  const [localPreviewConfigByMatch, setLocalPreviewConfigByMatch] = useState({});
  const [joinLinksByMatchTeam, setJoinLinksByMatchTeam] = useState({});
  const [joinStatusByMatchTeam, setJoinStatusByMatchTeam] = useState({});
  const [joinLinkStatusByMatchTeam, setJoinLinkStatusByMatchTeam] = useState({});
  const [isGeneratingJoinLinkByMatchTeam, setIsGeneratingJoinLinkByMatchTeam] = useState({});

  // UI state only
  const [activeTabs, setActiveTabs] = useState({});
  const [expandedMatches, setExpandedMatches] = useState({});
  const [playerSearch, setPlayerSearch] = useState({});

  const isLocalDev = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location?.hostname);

  const copyText = async (value, successMessage = 'Copied.') => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setSaveMessage(successMessage);
    } catch (error) {
      setErrorMessage('Unable to copy value.');
    }
  };
  const getJoinKey = (matchIdOrLocalId, teamKey) => `${matchIdOrLocalId}:${teamKey}`;
  const getSelectedJoinStatus = (match, teamKey) => normalizeJoinStatus(joinStatusByMatchTeam[getJoinKey(match?.id || match?.localId, teamKey)]);
  const getJoinStatusLabel = (status) => {
    if (status === 'playing') return 'Playing';
    if (status === 'substitute') return 'Substitute';
    return 'Unannounced';
  };

  const handleGenerateTeamJoinLink = async (match, teamKey) => {
    const matchIdOrLocalId = match?.id || match?.localId;
    const key = getJoinKey(matchIdOrLocalId, teamKey);
    if (!match?.id) {
      setJoinLinkStatusByMatchTeam((prev) => ({ ...prev, [key]: 'Save match first to generate join link.' }));
      return '';
    }
    if (!token || !tournamentId) return '';

    setIsGeneratingJoinLinkByMatchTeam((prev) => ({ ...prev, [key]: true }));
    setJoinLinkStatusByMatchTeam((prev) => ({ ...prev, [key]: '' }));
    const joinStatus = normalizeJoinStatus(getSelectedJoinStatus(match, teamKey));
    try {
      const response = await generateTeamJoinLink({ token, tournamentId, matchId: match.id, teamKey, joinStatus });
      const joinUrl = response?.joinUrl || '';
      const resolvedStatus = normalizeJoinStatus(response?.joinStatus || joinStatus);
      setJoinLinksByMatchTeam((prev) => ({ ...prev, [key]: joinUrl }));
      setJoinStatusByMatchTeam((prev) => ({ ...prev, [key]: resolvedStatus }));
      setJoinLinkStatusByMatchTeam((prev) => ({ ...prev, [key]: joinUrl ? `Join link generated for ${getJoinStatusLabel(resolvedStatus)}.` : 'No join link returned.' }));
      return joinUrl;
    } catch (error) {
      setJoinLinkStatusByMatchTeam((prev) => ({ ...prev, [key]: error?.message || 'Unable to generate join link.' }));
      return '';
    } finally {
      setIsGeneratingJoinLinkByMatchTeam((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleCopyJoinLink = async (match, teamKey) => {
    const matchIdOrLocalId = match?.id || match?.localId;
    const key = getJoinKey(matchIdOrLocalId, teamKey);
    if (!match?.id) {
      setJoinLinkStatusByMatchTeam((prev) => ({ ...prev, [key]: 'Save match first to generate join link.' }));
      return;
    }
    let joinUrl = joinLinksByMatchTeam[key];
    if (!joinUrl) {
      joinUrl = await handleGenerateTeamJoinLink(match, teamKey);
    }
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setJoinLinkStatusByMatchTeam((prev) => ({ ...prev, [key]: 'Join link copied.' }));
    } catch (error) {
      setJoinLinkStatusByMatchTeam((prev) => ({ ...prev, [key]: 'Unable to copy join link.' }));
    }
  };
  const matchFormatsPath = useMemo(() => {
    if (!managerId || !tournamentId) return '';
    if (communityId) {
      return `/community/${communityId}/match-manager/${managerId}/tournaments/${tournamentId}/match-formats`;
    }
    if (clubId) {
      return `/club/${clubId}/match-manager/${managerId}/tournaments/${tournamentId}/match-formats`;
    }
    if (organizationId) {
      return `/org/${organizationId}/match-manager/${managerId}/tournaments/${tournamentId}/match-formats`;
    }
    if (individualTournamentId) {
      return `/individual-tournament/${individualTournamentId}/match-manager/${managerId}/tournaments/${tournamentId}/match-formats`;
    }
    return '';
  }, [clubId, communityId, individualTournamentId, managerId, organizationId, tournamentId]);

  const matchManagerScopeParams = useMemo(() => ({
    organizationId,
    communityId,
    clubId,
    individualTournamentId,
    managerId,
    tournamentId,
  }), [clubId, communityId, individualTournamentId, managerId, organizationId, tournamentId]);

  const matchBasePath = useMemo(
    () => buildMatchManagerMatchesPath(matchManagerScopeParams),
    [matchManagerScopeParams]
  );

  useEffect(() => {
    const loadSchedule = async () => {
      if (!token || !tournamentId) {
        setErrorMessage('Login required to view manual schedules.');
        setTournament(null);
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      try {
      const response = await fetchMatchSchedule({ token, tournamentId });
        setTournament(response.tournament || null);

        const { schedule } = extractMatchSchedulePayload(response);
        if (schedule.length > 0) {
          const normalizedMatches = schedule.map((match, index) => normalizeMatch(match, index));
          setMatches(normalizedMatches);
          const initialJoinLinks = {};
          const initialJoinStatuses = {};
          normalizedMatches.forEach((m) => {
            const matchKey = m.id || m.localId;
            const links = m?.configuration?.playerJoinLinks || {};
            ['team1', 'team2'].forEach((teamKey) => {
              const joinKey = getJoinKey(matchKey, teamKey);
              if (links?.[teamKey]?.link) initialJoinLinks[joinKey] = links[teamKey].link;
              if (links?.[teamKey]?.joinStatus) initialJoinStatuses[joinKey] = normalizeJoinStatus(links[teamKey].joinStatus);
            });
          });
          setJoinLinksByMatchTeam(initialJoinLinks);
          setJoinStatusByMatchTeam(initialJoinStatuses);

          const firstKey = normalizedMatches[0]?.localId;
          setExpandedMatches(firstKey ? { [firstKey]: true } : {});
        } else {
          const emptyMatch = createEmptyMatch();
          setMatches([emptyMatch]);
          setExpandedMatches({ [emptyMatch.localId]: true });
        }

        setIsDirty(false);
      } catch (error) {
        setErrorMessage(error.message || 'Unable to load match schedule.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSchedule();
  }, [individualTournamentId, isIndividualTournamentRoute, managerId, token, tournamentId]);

  useEffect(() => {
    if (!token || !tournamentId) return;
    const savedMatches = matches.filter((match) => match?.id);
    savedMatches.forEach((match) => {
      const matchKey = match.localId;
      fetchMatchStreamDetails({ token, tournamentId, matchId: match.id })
        .then((details) => setStreamDetailsByMatch((prev) => ({ ...prev, [matchKey]: details || null })))
        .catch(() => setStreamDetailsByMatch((prev) => ({ ...prev, [matchKey]: null })));

      fetchMatchLiveStatus({ token, tournamentId, matchId: match.id })
        .then((status) => {
          setLiveStatusByMatch((prev) => ({ ...prev, [matchKey]: status || null }));
          setLiveStatusErrorByMatch((prev) => ({ ...prev, [matchKey]: '' }));
        })
        .catch((error) => {
          setLiveStatusByMatch((prev) => ({ ...prev, [matchKey]: null }));
          setLiveStatusErrorByMatch((prev) => ({ ...prev, [matchKey]: error?.message || 'Unable to load live status.' }));
        });

      const mode = normalizeStreamingMode(match.configuration?.streamingConfig?.mode || match.configuration?.streamingMode);
      if (mode === STREAMING_MODES.OBS) {
        fetchRelayStatus({ token, tournamentId, matchId: match.id })
          .then((relay) => setRelayStatusByMatch((prev) => ({ ...prev, [matchKey]: relay?.relayStatus || null })))
          .catch(() => setRelayStatusByMatch((prev) => ({ ...prev, [matchKey]: null })));
      }
    });
  }, [matches, token, tournamentId]);

  useEffect(() => {
    const loadRegisteredPlayers = async () => {
      if (!token || !tournamentId) {
        setRegisteredPlayers([]);
        return;
      }

      setIsPlayersLoading(true);
      setPlayersError('');

      try {
        const response = await fetchTournamentRegisteredPlayers(
          tournamentId,
          token,
          organizationId,
          { communityId, clubId }
        );

        const roster = Array.isArray(response?.players) ? response.players : [];
        const normalizedRoster = roster
          .filter((player) => hasTournamentRole(player, 'Player'))
          .map((player, index) => {
            const playerId = getPlayerId(player);
            if (!playerId) return null;
            return {
              id: playerId,
              name:
                player?.name ||
                player?.fullName ||
                player?.personal?.fullName ||
                player?.user?.name ||
                `Player ${index + 1}`,
              role: player?.role || player?.primaryRole || player?.skills?.primaryRole || '',
            };
          })
          .filter(Boolean);

        setRegisteredPlayers(normalizedRoster);
      } catch (error) {
        setPlayersError(error.message || 'Unable to load registered players.');
        setRegisteredPlayers([]);
      } finally {
        setIsPlayersLoading(false);
      }
    };

    loadRegisteredPlayers();
  }, [clubId, communityId, organizationId, token, tournamentId]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const handleMatchChange = (index, field, value) => {
    setIsDirty(true);
    setMatches((prev) => prev.map((match, idx) => (idx === index ? { ...match, [field]: value } : match)));
  };

  const handleConfigChange = (index, field, value) => {
    setIsDirty(true);
    setMatches((prev) =>
      prev.map((match, idx) => {
        if (idx !== index) return match;
        const currentConfig = match.configuration || {};
        return { ...match, configuration: { ...currentConfig, [field]: value } };
      })
    );
  };

  const getLogoUploadState = (matchKey, teamKey) => logoUploadState[`${matchKey}:${teamKey}`] || {};

  const setLogoUploadField = (matchKey, teamKey, key, value) => {
    const stateKey = `${matchKey}:${teamKey}`;
    setLogoUploadState((prev) => ({
      ...prev,
      [stateKey]: {
        ...(prev[stateKey] || {}),
        [key]: value,
      },
    }));
  };

  const handleTeamLogoUpload = async (index, teamKey, file) => {
    if (!file || !token || !tournamentId) return;
    if (!file.type?.startsWith('image/')) {
      const localId = matches[index]?.localId || `match-${index}`;
      setLogoUploadField(localId, teamKey, 'error', 'Please select a valid image file.');
      return;
    }

    const logoField = teamKey === 'team1' ? 'team1Logo' : 'team2Logo';
    const localId = matches[index]?.localId || `match-${index}`;
    setLogoUploadField(localId, teamKey, 'error', '');
    setLogoUploadField(localId, teamKey, 'isUploading', true);

    try {
      const previousLogoUrl = matches[index]?.[logoField] || '';
      const response = await uploadMatchTeamLogo(tournamentId, file, token, previousLogoUrl);
      const uploadedLogoUrl = response.logoUrl || '';
      setIsDirty(true);
      setMatches((prev) =>
        prev.map((match, idx) => {
          if (idx !== index) return match;
          if (teamKey === 'team1') {
            return {
              ...match,
              team1Logo: uploadedLogoUrl || match.team1Logo,
              team2Logo: match.team2Logo,
            };
          }
          return {
            ...match,
            team1Logo: match.team1Logo,
            team2Logo: uploadedLogoUrl || match.team2Logo,
          };
        })
      );
    } catch (error) {
      setLogoUploadField(localId, teamKey, 'error', error.message || 'Unable to upload team logo right now.');
    } finally {
      setLogoUploadField(localId, teamKey, 'isUploading', false);
    }
  };

  const handleStreamingChange = (index, field, value) => {
    setIsDirty(true);
    setMatches((prev) =>
      prev.map((match, idx) => {
        if (idx !== index) return match;

        const currentStreaming =
          match.configuration.streamingConfig || createEmptyConfiguration().streamingConfig;

        if (field === 'streamingMode') {
          const nextMode = normalizeStreamingMode(value);
          const nextProvider = normalizeStreamProvider(match.configuration.streamProvider);
          return {
            ...match,
            configuration: {
              ...match.configuration,
              streamingMode: nextMode,
              streamProvider:
                nextMode === STREAMING_MODES.DIRECT_MOBILE
                  ? (nextProvider === STREAM_PROVIDERS.FACEBOOK ? STREAM_PROVIDERS.FACEBOOK : STREAM_PROVIDERS.YOUTUBE)
                  : nextProvider,
              streamingConfig: { ...currentStreaming, mode: nextMode },
            },
          };
        }

        if (field === 'streamProvider') {
          return {
            ...match,
            configuration: {
              ...match.configuration,
              streamProvider: normalizeStreamProvider(value),
            },
          };
        }

        if (field === 'youtubeIngestUrl') {
          return {
            ...match,
            configuration: {
              ...match.configuration,
              youtubeIngestUrl: value,
              streamingConfig: {
                ...currentStreaming,
                youtube: { ...currentStreaming.youtube, ingestUrl: value },
              },
            },
          };
        }

        if (field === 'livePlaybackUrl') {
          return {
            ...match,
            configuration: {
              ...match.configuration,
              livePlaybackUrl: value,
              streamUrl: value,
              streamingConfig: {
                ...currentStreaming,
                youtube: { ...currentStreaming.youtube, playbackUrl: value },
                obs: { ...currentStreaming.obs, playbackUrl: value },
              },
            },
          };
        }

        if (field === 'youtubeVideoId') {
          return {
            ...match,
            configuration: {
              ...match.configuration,
              youtubeVideoId: value,
              streamingConfig: {
                ...currentStreaming,
                youtube: { ...currentStreaming.youtube, videoId: value },
              },
            },
          };
        }

        if (field === 'youtubeLiveLink') {
          return {
            ...match,
            configuration: {
              ...match.configuration,
              youtubeLiveLink: value,
              livePlaybackUrl: value,
              streamUrl: value,
              streamingConfig: {
                ...currentStreaming,
                youtube: { ...currentStreaming.youtube, playbackUrl: value },
              },
            },
          };
        }

        if (field === 'overlayMode') {
          return {
            ...match,
            configuration: {
              ...match.configuration,
              overlayMode: value,
              streamingConfig: { ...currentStreaming, overlayMode: value },
            },
          };
        }

        if (field === 'rtmpIngestUrl') {
          const existingObsConfig = currentStreaming?.obsConfig || {};
          const cameras = buildObsCameras({
            matchIdentifier: match.id || match.localId,
            ingestUrl: value,
            cameraCount: existingObsConfig.cameraCount,
            cameras: existingObsConfig.cameras,
          });
          return {
            ...match,
            configuration: {
              ...match.configuration,
              rtmpIngestUrl: value,
              streamingConfig: {
                ...currentStreaming,
                local: { ...currentStreaming.local, ingestUrl: value },
                obs: { ...currentStreaming.obs, ingestUrl: value },
                obsConfig: {
                  cameraCount: normalizeCameraCount(existingObsConfig.cameraCount),
                  ingestUrl: value,
                  cameras,
                },
              },
            },
          };
        }

        if (field === 'rtmpStreamKey') {
          return {
            ...match,
            configuration: {
              ...match.configuration,
              rtmpStreamKey: value,
              streamingConfig: {
                ...currentStreaming,
                local: { ...currentStreaming.local, streamKey: value },
                obs: { ...currentStreaming.obs, streamKey: value },
              },
            },
          };
        }

        if (field === 'obsCameraCount') {
          const cameraCount = normalizeCameraCount(value);
          const ingestUrl = (currentStreaming?.obsConfig?.ingestUrl || currentStreaming?.obs?.ingestUrl || '').trim();
          return {
            ...match,
            configuration: {
              ...match.configuration,
              streamingConfig: {
                ...currentStreaming,
                obsConfig: {
                  cameraCount,
                  ingestUrl,
                  cameras: buildObsCameras({
                    matchIdentifier: match.id || match.localId,
                    ingestUrl,
                    cameraCount,
                    cameras: currentStreaming?.obsConfig?.cameras,
                  }),
                },
              },
            },
          };
        }

        return match;
      })
    );
  };

  const toggleSquadPlayer = (index, squadKey, playingKey, playerId) => {
    const match = matches[index];
    const current = Array.isArray(match?.configuration?.[squadKey]) ? match.configuration[squadKey] : [];
    const exists = current.includes(playerId);

    setErrorMessage('');
    setIsDirty(true);
    setMatches((prev) => prev.map((currentMatch, idx) => {
      if (idx !== index) return currentMatch;
      const currentSquad = Array.isArray(currentMatch.configuration[squadKey]) ? currentMatch.configuration[squadKey] : [];
      const nextSquad = currentSquad.includes(playerId)
        ? currentSquad.filter((id) => id !== playerId)
        : [...currentSquad, playerId];
      return {
        ...currentMatch,
        configuration: updateTeamSelection({
          configuration: currentMatch.configuration,
          squadKey,
          playingKey,
          nextSquad,
        }),
      };
    }));
  };

  const changePlayerStatus = (index, squadKey, playingKey, playerId, nextStatus) => {
    const match = matches[index];
    if (!match) return;
    const { substituteKey } = getTeamStatusKeys(playingKey);
    const configuration = match.configuration || {};
    const playing = uniquePlayers(configuration[playingKey]);
    const substitutes = uniquePlayers(configuration[substituteKey]);
    const playingLimit = Number(configuration.playingCount ?? configuration.playersPerSide) || 0;
    const squadSize = Number(configuration.squadSize) || playingLimit || 0;
    const nextPlayingCount = playing.filter((id) => id !== playerId).length + (nextStatus === 'playing' ? 1 : 0);
    const nextActiveCount = [...new Set([
      ...playing.filter((id) => id !== playerId),
      ...substitutes.filter((id) => id !== playerId),
      ...(nextStatus === 'playing' || nextStatus === 'substitute' ? [playerId] : []),
    ])].length;

    if (playingLimit > 0 && nextPlayingCount > playingLimit) {
      setErrorMessage(`Only ${playingLimit} players can have Playing status for this team.`);
      return;
    }
    if (squadSize > 0 && nextActiveCount > squadSize) {
      setErrorMessage(`Only ${squadSize} players can have Playing or Substitute status for this team.`);
      return;
    }

    setErrorMessage('');
    setIsDirty(true);
    setMatches((prev) => prev.map((currentMatch, idx) => {
      if (idx !== index) return currentMatch;
      const keys = getTeamStatusKeys(playingKey);
      const nextConfiguration = { ...currentMatch.configuration };
      [playingKey, keys.substituteKey, keys.unannouncedKey].forEach((key) => {
        nextConfiguration[key] = uniquePlayers(nextConfiguration[key]).filter((id) => id !== playerId);
      });
      const statusKey = nextStatus === 'playing'
        ? playingKey
        : nextStatus === 'substitute' ? keys.substituteKey : keys.unannouncedKey;
      nextConfiguration[statusKey] = [...nextConfiguration[statusKey], playerId];
      return { ...currentMatch, configuration: nextConfiguration };
    }));
  };

  const getPublicViewUrl = (matchId) => {
    if (!matchId || !tournamentId) return '';
    const path = `/public/tournaments/${tournamentId}/matches/${matchId}`;
    if (typeof window === 'undefined' || !window.location?.origin) return path;
    return `${window.location.origin}${path}`;
  };

  const handleCopyPublicView = async (matchId, url) => {
    if (!matchId || !url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus((prev) => ({ ...prev, [matchId]: 'Link copied.' }));
    } catch (error) {
      setCopyStatus((prev) => ({ ...prev, [matchId]: 'Unable to copy link.' }));
    }
  };

  const handleAddMatch = () => {
    setIsDirty(true);
    setMatches((prev) => [...prev, createEmptyMatch(prev.length + 1, prev.length + 1)]);
  };

  const handleRemoveMatch = (index) => {
    setIsDirty(true);
    setMatches((prev) => {
      const target = prev[index];
      const filtered = prev.filter((_, idx) => idx !== index);

      if (target?.localId) {
        setExpandedMatches((expanded) => {
          const next = { ...expanded };
          delete next[target.localId];

          if (filtered.length > 0 && !Object.values(next).some(Boolean)) {
            next[filtered[0].localId] = true;
          }
          return next;
        });
      }

      return filtered;
    });
  };

  const handleSave = async () => {
    if (!token || !tournamentId) return;

    setIsSaving(true);
    setErrorMessage('');
    setSaveMessage('');

    const normalizeFixtureNumber = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : '';
    };

    for (let index = 0; index < matches.length; index += 1) {
      const match = matches[index];
      const playersPerSide = Number(match.configuration.playingCount ?? match.configuration.playersPerSide) || 0;
      const team1Squad = Array.isArray(match.configuration.team1SquadPlayers) ? match.configuration.team1SquadPlayers : [];
      const team2Squad = Array.isArray(match.configuration.team2SquadPlayers) ? match.configuration.team2SquadPlayers : [];
      const squadSize = Math.max(team1Squad.length, team2Squad.length);

      if (playersPerSide && squadSize && squadSize < playersPerSide) {
        setIsSaving(false);
        setErrorMessage(`Match ${index + 1}: Squad size must be greater than or equal to Playing Players Per Side.`);
        return;
      }
      if (playersPerSide && (
        (team1Squad.length > 0 && team1Squad.length < playersPerSide)
        || (team2Squad.length > 0 && team2Squad.length < playersPerSide)
      )) {
        setIsSaving(false);
        setErrorMessage('Players per side cannot exceed the number of players selected in either squad.');
        return;
      }
      const teamStatuses = [
        deriveManualTeamStatus({
          squadPlayers: team1Squad,
          playingPlayers: match.configuration.team1PlayingPlayers,
          substitutePlayers: match.configuration.team1SubstitutePlayers,
          unannouncedPlayers: match.configuration.team1UnannouncedPlayers,
          playingCount: playersPerSide,
          squadSize,
        }),
        deriveManualTeamStatus({
          squadPlayers: team2Squad,
          playingPlayers: match.configuration.team2PlayingPlayers,
          substitutePlayers: match.configuration.team2SubstitutePlayers,
          unannouncedPlayers: match.configuration.team2UnannouncedPlayers,
          playingCount: playersPerSide,
          squadSize,
        }),
      ];
      const invalidPlayingTeam = teamStatuses.findIndex((status) => playersPerSide > 0 && status.playing.length > playersPerSide);
      const invalidSquadTeam = teamStatuses.findIndex((status) => squadSize > 0 && status.squad.length > squadSize);
      const invalidActiveTeam = teamStatuses.findIndex((status) => squadSize > 0 && status.announced.length > squadSize);
      if (invalidPlayingTeam >= 0 || invalidSquadTeam >= 0 || invalidActiveTeam >= 0) {
        setIsSaving(false);
        const teamNumber = [invalidPlayingTeam, invalidSquadTeam, invalidActiveTeam].find((teamIndex) => teamIndex >= 0) + 1;
        setErrorMessage(`Match ${index + 1}, Team ${teamNumber}: player statuses exceed the configured playing or squad limit.`);
        return;
      }
    }

    let schedule = matches.map((match, index) => {
      const normalizedMode = normalizeStreamingMode(match.configuration.streamingConfig?.mode || match.configuration.streamingMode);
      const normalizedProvider = normalizeStreamProvider(match.configuration.streamProvider);
      const currentStreamingConfig = match.configuration.streamingConfig || createEmptyConfiguration().streamingConfig;
      const streamingConfig = {
        ...currentStreamingConfig,
        mode: normalizedMode,
        overlayMode: currentStreamingConfig?.overlayMode || match.configuration.overlayMode || 'burn_in',
        youtube: {
          ...currentStreamingConfig.youtube,
          ingestUrl: currentStreamingConfig?.youtube?.ingestUrl || match.configuration.youtubeIngestUrl || 'rtmp://a.rtmp.youtube.com/live2',
          streamKey: currentStreamingConfig?.youtube?.streamKey || match.configuration.youtubeStreamKey || match.configuration.rtmpStreamKey || '',
          playbackUrl: currentStreamingConfig?.youtube?.playbackUrl || match.configuration.youtubeLiveLink || match.configuration.livePlaybackUrl || match.configuration.streamUrl || '',
          videoId: currentStreamingConfig?.youtube?.videoId || match.configuration.youtubeVideoId || '',
        },
        local: {
          ...currentStreamingConfig.local,
          ingestUrl: currentStreamingConfig?.local?.ingestUrl || match.configuration.rtmpIngestUrl || 'rtmp://192.168.29.26:1935/stream',
          streamKey: currentStreamingConfig?.local?.streamKey || match.configuration.rtmpStreamKey || '',
        },
        obs: {
          ...currentStreamingConfig.obs,
          ingestUrl: currentStreamingConfig?.obs?.ingestUrl || match.configuration.rtmpIngestUrl || 'rtmp://192.168.29.26:1935/stream',
          streamKey: currentStreamingConfig?.obs?.streamKey || match.configuration.rtmpStreamKey || '',
          playbackUrl: currentStreamingConfig?.obs?.playbackUrl || match.configuration.livePlaybackUrl || match.configuration.streamUrl || '',
        },
        obsConfig: {
          cameraCount: normalizeCameraCount(currentStreamingConfig?.obsConfig?.cameraCount),
          ingestUrl: currentStreamingConfig?.obsConfig?.ingestUrl || currentStreamingConfig?.obs?.ingestUrl || match.configuration.rtmpIngestUrl || 'rtmp://192.168.29.26:1935/stream',
          cameras: buildObsCameras({
            matchIdentifier: match.id || match.localId,
            ingestUrl: currentStreamingConfig?.obsConfig?.ingestUrl || currentStreamingConfig?.obs?.ingestUrl || match.configuration.rtmpIngestUrl || 'rtmp://192.168.29.26:1935/stream',
            cameraCount: normalizeCameraCount(currentStreamingConfig?.obsConfig?.cameraCount),
            cameras: currentStreamingConfig?.obsConfig?.cameras,
          }),
        },
      };
      const playersPerSide = normalizeFixtureNumber(match.configuration.playingCount ?? match.configuration.playersPerSide);
      const squadSize = Math.max(
        uniquePlayers(match.configuration.team1SquadPlayers).length,
        uniquePlayers(match.configuration.team2SquadPlayers).length,
      );
      const team1Status = deriveManualTeamStatus({
        squadPlayers: match.configuration.team1SquadPlayers,
        playingPlayers: match.configuration.team1PlayingPlayers,
        substitutePlayers: match.configuration.team1SubstitutePlayers,
        unannouncedPlayers: match.configuration.team1UnannouncedPlayers,
        playingCount: playersPerSide,
        squadSize,
      });
      const team2Status = deriveManualTeamStatus({
        squadPlayers: match.configuration.team2SquadPlayers,
        playingPlayers: match.configuration.team2PlayingPlayers,
        substitutePlayers: match.configuration.team2SubstitutePlayers,
        unannouncedPlayers: match.configuration.team2UnannouncedPlayers,
        playingCount: playersPerSide,
        squadSize,
      });
      const sanitizeLeader = (playerId, allowedPool = []) => (allowedPool.includes(playerId) ? playerId : '');

      const existingMatch = match || {};
      return {
        ...(match.id ? { id: match.id } : {}),
        day: Number(match.day) || 1,
        dayLabel: match.dayLabel,
        date: match.date || null,
        time: match.time,
        matchNumber: Number(match.matchNumber) || index + 1,
        team1: match.team1,
        team2: match.team2,
        team1Logo:
          match.team1Logo ||
          existingMatch?.team1Logo ||
          existingMatch?.configuration?.team1Logo ||
          '',
        team2Logo:
          match.team2Logo ||
          existingMatch?.team2Logo ||
          existingMatch?.configuration?.team2Logo ||
          '',
        configuration: {
          ...createEmptyConfiguration(),
          ...(match.configuration || {}),
          venue: match.configuration.venue,
          notes: match.configuration.notes,
          scorer: match.configuration.scorer,
          umpire1: match.configuration.umpire1,
          umpire2: match.configuration.umpire2,
          matchReferee: match.configuration.matchReferee,
          streamUrl: match.configuration.streamUrl || streamingConfig.youtube.playbackUrl,
          youtubeLiveLink:
            match.configuration.youtubeLiveLink ||
            streamingConfig.youtube.playbackUrl ||
            match.configuration.livePlaybackUrl ||
            match.configuration.streamUrl ||
            '',
          rtmpStreamKey:
            normalizedMode === STREAMING_MODES.DIRECT_MOBILE
              ? streamingConfig.youtube.streamKey
              : (normalizedMode === STREAMING_MODES.OBS
                ? streamingConfig.obs.streamKey
                : streamingConfig.local.streamKey),
          youtubeStreamKey: streamingConfig.youtube.streamKey,
          youtubeIngestUrl: streamingConfig.youtube.ingestUrl,
          rtmpIngestUrl:
            normalizedMode === STREAMING_MODES.LOCAL_MAC_TESTING
              ? streamingConfig.local.ingestUrl
              : streamingConfig.obs.ingestUrl,
          streamProvider: normalizedMode === STREAMING_MODES.LOCAL_MAC_TESTING ? 'local_preview' : normalizedProvider,
          streamingMode: normalizedMode,
          streamingConfig,
          livePlaybackUrl: streamingConfig.youtube.playbackUrl || match.configuration.livePlaybackUrl,
          youtubePlaybackUrl: streamingConfig.youtube.playbackUrl || match.configuration.livePlaybackUrl,
          youtubeVideoId: streamingConfig.youtube.videoId || match.configuration.youtubeVideoId,
          youtubeBroadcastId: match.configuration.youtubeBroadcastId || '',
          youtubeStreamId: match.configuration.youtubeStreamId || '',
          youtubeStatus: match.configuration.youtubeStatus || '',
          overlayMode: streamingConfig.overlayMode,
          overlayWebUrl: match.configuration.overlayWebUrl,
          overlayAuthToken: match.configuration.overlayAuthToken,
          tossWinner: (match.configuration.tossWinner || '').trim(),
          tossDecision: (match.configuration.tossDecision || '').trim().toLowerCase(),
          overs: normalizeFixtureNumber(match.configuration.overs),
          maxOversPerPlayer: normalizeFixtureNumber(match.configuration.maxOversPerPlayer),
          maxPlayersAtMaxOvers: normalizeFixtureNumber(match.configuration.maxPlayersAtMaxOvers),
          powerPlayStartOver: normalizeFixtureNumber(match.configuration.powerPlayStartOver),
          powerPlayEndOver: normalizeFixtureNumber(match.configuration.powerPlayEndOver),
          playersPerSide,
          playingCount: playersPerSide,
          squadSize,
          allowLastBatsman: Boolean(match.configuration.allowLastBatsman),
          team1SquadPlayers: match.configuration.team1SquadPlayers,
          team2SquadPlayers: match.configuration.team2SquadPlayers,
          team1PlayingPlayers: team1Status.playing,
          team2PlayingPlayers: team2Status.playing,
          team1SubstitutePlayers: team1Status.substitutes,
          team2SubstitutePlayers: team2Status.substitutes,
          team1UnannouncedPlayers: team1Status.unannounced,
          team2UnannouncedPlayers: team2Status.unannounced,
          team1MatchPlayers: team1Status.matchPlayers,
          team2MatchPlayers: team2Status.matchPlayers,
          team1AnnouncedPlayers: team1Status.announced,
          team2AnnouncedPlayers: team2Status.announced,
          team1Players: match.configuration.team1SquadPlayers,
          team2Players: match.configuration.team2SquadPlayers,
          team1Captain: sanitizeLeader(match.configuration.team1Captain, team1Status.squad),
          team1ViceCaptain: sanitizeLeader(match.configuration.team1ViceCaptain, team1Status.squad),
          team1WicketKeeper: sanitizeLeader(match.configuration.team1WicketKeeper, team1Status.squad),
          team2Captain: sanitizeLeader(match.configuration.team2Captain, team2Status.squad),
          team2ViceCaptain: sanitizeLeader(match.configuration.team2ViceCaptain, team2Status.squad),
          team2WicketKeeper: sanitizeLeader(match.configuration.team2WicketKeeper, team2Status.squad),
          playerJoinLinks: {
            ...(match.configuration.playerJoinLinks || {}),
            team1: {
              ...((match.configuration.playerJoinLinks || {}).team1 || {}),
              link: joinLinksByMatchTeam[getJoinKey(match.id || match.localId, 'team1')] || (match.configuration.playerJoinLinks || {}).team1?.link || '',
              joinStatus: normalizeJoinStatus(getSelectedJoinStatus(match, 'team1')),
            },
            team2: {
              ...((match.configuration.playerJoinLinks || {}).team2 || {}),
              link: joinLinksByMatchTeam[getJoinKey(match.id || match.localId, 'team2')] || (match.configuration.playerJoinLinks || {}).team2?.link || '',
              joinStatus: normalizeJoinStatus(getSelectedJoinStatus(match, 'team2')),
            },
          },
        },
      };
    });

    schedule = schedule.map((match) => ({
      ...match,
      configuration: normalizeMatchConfig(match.configuration || {}),
    }));

    schedule.forEach((match) => {
    });

    const daysNeeded = schedule.reduce((max, match) => Math.max(max, Number(match.day) || 1), 1);

    try {
      const response = await saveManualMatchSchedule({
        token,
        tournamentId,
        payload: {
          schedule,
          meta: {
            format: 'manual',
            totalMatches: schedule.length,
            daysNeeded,
            blockSize: null,
          },
        },
      });

      const { schedule: savedSchedule } = extractMatchSchedulePayload(response);
      savedSchedule.forEach((match) => {
      });
      if (savedSchedule.length > 0) {
        const normalizedMatches = savedSchedule.map((match, index) => normalizeMatch(match, index));
        setMatches(normalizedMatches);

        setExpandedMatches((prev) => {
          if (Object.keys(prev).length > 0) return prev;
          const firstKey = normalizedMatches[0]?.localId;
          return firstKey ? { [firstKey]: true } : {};
        });
      }

      setIsDirty(false);
      setSaveMessage('Manual schedule saved successfully.');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to save manual schedule.');
    } finally {
      setIsSaving(false);
    }
  };

  const refreshLiveStatus = async (match) => {
    if (!match?.id || !token || !tournamentId) return;
    try {
      const data = await fetchMatchLiveStatus({ token, tournamentId, matchId: match.id });
      setLiveStatusByMatch((prev) => ({ ...prev, [match.localId]: data || null }));
      setLiveStatusErrorByMatch((prev) => ({ ...prev, [match.localId]: '' }));
    } catch (error) {
      setLiveStatusErrorByMatch((prev) => ({ ...prev, [match.localId]: error?.message || 'Unable to load live status.' }));
    }
  };

  const handleGeneratePublicStreamUrl = async (match, index) => {
    if (!match?.id || !token || !tournamentId) {
      setErrorMessage('Save manual match schedule before generating YouTube details.');
      return;
    }

    setIsGeneratingYouTubeByMatch((prev) => ({ ...prev, [match.localId]: true }));
    setStreamUrlStatusByMatch((prev) => ({ ...prev, [match.localId]: 'Generating YouTube live details...' }));

    try {
      const response = await generateMatchYouTubeStream({ token, tournamentId, matchId: match.id });
      const resolvedLink = resolveYouTubeLiveLinkFromResponse(response);
      const fallbackLink = resolveYouTubePublicStreamUrl(
        response?.youtubeStreamKey || match.configuration?.streamingConfig?.youtube?.streamKey,
        response?.rtmpStreamKey || match.configuration?.streamingConfig?.local?.streamKey,
      );
      const liveLink = resolvedLink || fallbackLink;
      if (!liveLink) throw new Error('YouTube stream generated, but no public live link was returned.');

      handleStreamingChange(index, 'youtubeLiveLink', liveLink);
      handleStreamingChange(index, 'youtubeVideoId', response?.youtubeVideoId || '');
      if (response?.youtubeIngestUrl) handleStreamingChange(index, 'youtubeIngestUrl', response.youtubeIngestUrl);
      if (response?.youtubeStreamKey) handleStreamingChange(index, 'rtmpStreamKey', response.youtubeStreamKey);

      setStreamUrlStatusByMatch((prev) => ({ ...prev, [match.localId]: 'YouTube live link generated and filled automatically.' }));
      setStreamDetailsByMatch((prev) => ({
        ...prev,
        [match.localId]: {
          ...(prev?.[match.localId] || {}),
          ...(response || {}),
          youtube: {
            ...((prev?.[match.localId] || {}).youtube || {}),
            playbackUrl: liveLink,
            videoId: response?.youtubeVideoId || '',
            streamKey: response?.youtubeStreamKey || ((prev?.[match.localId] || {}).youtube || {}).streamKey || '',
            streamId: response?.youtubeStreamId || ((prev?.[match.localId] || {}).youtube || {}).streamId || '',
            broadcastId: response?.youtubeBroadcastId || ((prev?.[match.localId] || {}).youtube || {}).broadcastId || '',
          },
          streamProvider: response?.streamProvider || (prev?.[match.localId] || {}).streamProvider || 'youtube',
        },
      }));
      const details = await fetchMatchStreamDetails({ token, tournamentId, matchId: match.id }).catch(() => null);
      if (details) {
        setStreamDetailsByMatch((prev) => ({ ...prev, [match.localId]: details }));
      }
    } catch (error) {
      setErrorMessage(error.message || 'Unable to generate YouTube stream details.');
      setStreamUrlStatusByMatch((prev) => ({ ...prev, [match.localId]: '' }));
    } finally {
      setIsGeneratingYouTubeByMatch((prev) => ({ ...prev, [match.localId]: false }));
    }
  };

  const handleStartRelay = async (match) => {
    if (!match?.id || !token || !tournamentId) return;
    try {
      const data = await startRelay({ token, matchId: match.id, tournamentId });
      setRelayStatusByMatch((prev) => ({ ...prev, [match.localId]: data?.relayStatus || null }));
      setLocalPreviewStatusByMatch((prev) => ({ ...prev, [match.localId]: 'Relay started.' }));
    } catch (error) {
      setLocalPreviewStatusByMatch((prev) => ({ ...prev, [match.localId]: error.message || 'Unable to start relay.' }));
    }
  };

  const handleStopRelay = async (match) => {
    if (!match?.id || !token || !tournamentId) return;
    try {
      const data = await stopRelay({ token, matchId: match.id, tournamentId });
      setRelayStatusByMatch((prev) => ({ ...prev, [match.localId]: data?.relayStatus || null }));
      setLocalPreviewStatusByMatch((prev) => ({ ...prev, [match.localId]: 'Relay stopped.' }));
    } catch (error) {
      setLocalPreviewStatusByMatch((prev) => ({ ...prev, [match.localId]: error.message || 'Unable to stop relay.' }));
    }
  };

  const handleToggleLocalPreview = async (match) => {
    const matchKey = match.localId;
    const nextValue = !localPreviewEnabledByMatch[matchKey];
    setLocalPreviewEnabledByMatch((prev) => ({ ...prev, [matchKey]: nextValue }));
    if (!nextValue) {
      setLocalPreviewConfigByMatch((prev) => ({ ...prev, [matchKey]: null }));
      setLocalPreviewStatusByMatch((prev) => ({ ...prev, [matchKey]: '' }));
      return;
    }

    try {
      setLocalPreviewStatusByMatch((prev) => ({ ...prev, [matchKey]: 'Loading local preview details...' }));
      const response = await fetchLocalStreamPreview();
      if (!response?.enabled) {
        setLocalPreviewConfigByMatch((prev) => ({ ...prev, [matchKey]: null }));
        setLocalPreviewStatusByMatch((prev) => ({ ...prev, [matchKey]: 'Local preview disabled on server' }));
        return;
      }
      setLocalPreviewConfigByMatch((prev) => ({ ...prev, [matchKey]: response }));
      setLocalPreviewStatusByMatch((prev) => ({ ...prev, [matchKey]: 'Local preview configuration loaded.' }));
    } catch (error) {
      setLocalPreviewConfigByMatch((prev) => ({ ...prev, [matchKey]: null }));
      setLocalPreviewStatusByMatch((prev) => ({ ...prev, [matchKey]: error.message || 'Unable to load local preview configuration.' }));
    }
  };

  const findPlayerName = (playerId) => registeredPlayers.find((player) => player.id === playerId)?.name || '';

  // UI helpers only
  const getTab = (matchKey) => activeTabs[matchKey] || 'basics';
  const setTab = (matchKey, tab) => setActiveTabs((p) => ({ ...p, [matchKey]: tab }));

  const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-2">
      <div className="fw-semibold">{title}</div>
      {subtitle ? <div className="text-muted small">{subtitle}</div> : null}
    </div>
  );

  const scheduleDays = matches.reduce((max, match) => Math.max(max, Number(match.day) || 1), 1);
  const getSearchValue = (matchKey, teamKey) => playerSearch[`${matchKey}:${teamKey}`] || '';
  const setSearchValue = (matchKey, teamKey, value) =>
    setPlayerSearch((prev) => ({ ...prev, [`${matchKey}:${teamKey}`]: value }));

  const setPlayersForTeam = (index, squadKey, playingKey, ids) => {
    const match = matches[index];
    if (!match) return;
    const limitedIds = uniquePlayers(ids);
    setErrorMessage('');
    setIsDirty(true);
    setMatches((prev) => prev.map((currentMatch, idx) => (
      idx === index
        ? {
          ...currentMatch,
          configuration: updateTeamSelection({
            configuration: currentMatch.configuration,
            squadKey,
            playingKey,
            nextSquad: limitedIds,
          }),
        }
        : currentMatch
    )));
  };

  return (
    <div className="pb-5">
      <div className="top-0 bg-white border-bottom shadow-sm" style={{ zIndex: 1020 }}>
        <div className="container py-3 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
          <div>
            <div className="text-uppercase small text-primary fw-semibold">Match manager</div>
            <h1 className="h4 mb-0">Manual match format</h1>
            <div className="small text-muted">{tournament?.name || 'Tournament schedule workspace'}</div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            {matchFormatsPath && (
              <Link className="btn btn-outline-secondary" to={matchFormatsPath}>
                Back to match formats
              </Link>
            )}
            <button type="button" className="btn btn-outline-primary" onClick={handleAddMatch}>
              Add match
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="container pt-4">
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body py-2 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2">
            <div className="small">
              <strong>{tournament?.name || 'Manual schedule'}</strong> • Matches: <strong>{matches.length}</strong> •
              {' '}Days needed: <strong>{scheduleDays}</strong>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span
                className={`badge ${isSaving ? 'text-bg-warning' : isDirty ? 'text-bg-secondary' : 'text-bg-success'}`}
              >
                {isSaving ? 'Saving...' : isDirty ? 'Unsaved changes' : 'Saved'}
              </span>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setExpandedMatches(Object.fromEntries(matches.map((m) => [m.localId, true])))}
              >
                Expand all
              </button>

              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setExpandedMatches({})}>
                Collapse all
              </button>
            </div>
          </div>
        </div>

        {(errorMessage || saveMessage) && (
          <div className={`alert ${errorMessage ? 'alert-danger' : 'alert-success'}`} role="alert">
            {errorMessage || saveMessage}
          </div>
        )}

        {isLoading && (
          <div className="alert alert-secondary" role="alert">
            Loading manual schedule...
          </div>
        )}

        {(playersError || isPlayersLoading) && (
          <div className={`alert ${playersError ? 'alert-danger' : 'alert-secondary'}`} role="alert">
            {playersError || 'Loading registered players...'}
          </div>
        )}

        {!playersError && !isPlayersLoading && registeredPlayers.length === 0 && (
          <div className="alert alert-warning" role="alert">
            No registered players found for this tournament yet.
          </div>
        )}

        <div className="d-grid gap-3">
          {matches.map((match, index) => {
            const publicViewUrl = match.id ? getPublicViewUrl(match.id) : '';
            const matchCopyStatus = match.id ? copyStatus[match.id] : '';
            const playersLimit = Number(match.configuration.playingCount ?? match.configuration.playersPerSide) || 0;
            const squadLimit = Math.max(
              uniquePlayers(match.configuration.team1SquadPlayers).length,
              uniquePlayers(match.configuration.team2SquadPlayers).length,
            );

            const team1Squad = Array.isArray(match.configuration.team1SquadPlayers) ? match.configuration.team1SquadPlayers : [];
            const team2Squad = Array.isArray(match.configuration.team2SquadPlayers) ? match.configuration.team2SquadPlayers : [];
            const team1Status = deriveManualTeamStatus({
              squadPlayers: team1Squad,
              playingPlayers: match.configuration.team1PlayingPlayers,
              substitutePlayers: match.configuration.team1SubstitutePlayers,
              unannouncedPlayers: match.configuration.team1UnannouncedPlayers,
              playingCount: playersLimit,
              squadSize: squadLimit,
            });
            const team2Status = deriveManualTeamStatus({
              squadPlayers: team2Squad,
              playingPlayers: match.configuration.team2PlayingPlayers,
              substitutePlayers: match.configuration.team2SubstitutePlayers,
              unannouncedPlayers: match.configuration.team2UnannouncedPlayers,
              playingCount: playersLimit,
              squadSize: squadLimit,
            });
            const team1Playing = team1Status.playing;
            const team2Playing = team2Status.playing;
            const team1Substitutes = team1Status.substitutes;
            const team2Substitutes = team2Status.substitutes;

            const team1SquadOptions =
              team1Squad.length > 0
                ? registeredPlayers.filter((player) => team1Squad.includes(player.id) && hasTournamentRole(player, 'Player'))
                : registeredPlayers.filter((player) => hasTournamentRole(player, 'Player'));

            const team2SquadOptions =
              team2Squad.length > 0
                ? registeredPlayers.filter((player) => team2Squad.includes(player.id) && hasTournamentRole(player, 'Player'))
                : registeredPlayers.filter((player) => hasTournamentRole(player, 'Player'));
            const team1AnnouncedSet = new Set([...team1Playing, ...team1Substitutes]);
            const team2AnnouncedSet = new Set([...team2Playing, ...team2Substitutes]);
            const team1AnnouncedOptions = team1SquadOptions.filter((player) => team1AnnouncedSet.has(player.id));
            const team2AnnouncedOptions = team2SquadOptions.filter((player) => team2AnnouncedSet.has(player.id));

            const tab = getTab(match.localId);
            const isExpanded = Boolean(expandedMatches[match.localId]);

            const titleLine = `${match.team1 || 'Team 1'} vs ${match.team2 || 'Team 2'}`;
            const subLine = `Day ${match.day || 1} • ${match.date || 'TBD'} • ${match.time || 'TBD'} • Venue: ${
              match.configuration.venue || 'TBD'
            }`;

            const isConfigured = Boolean(match.team1 && match.team2 && match.configuration.overs);

            const team1Search = getSearchValue(match.localId, 'team1Players');
            const team2Search = getSearchValue(match.localId, 'team2Players');

            const { team1Options, team2Options } = getManualTeamPlayerOptions(
              registeredPlayers,
              team1Squad,
              team2Squad
            );

            const filteredTeam1 = team1Options.filter((player) =>
              player.name.toLowerCase().includes(team1Search.toLowerCase())
            );
            const filteredTeam2 = team2Options.filter((player) =>
              player.name.toLowerCase().includes(team2Search.toLowerCase())
            );

            const startResumeLabel = getStartResumeLabel(match);
            const matchId = normalizeId(match?.id || match?._id || match?.matchId);
            const startResumePath = getMatchStartResumePath({
              match: { ...match, id: matchId },
              matchBasePath,
              startPath: individualTournamentId
                ? buildMatchManagerMatchStartPath({ ...matchManagerScopeParams, matchId })
                : '',
              tossPath: individualTournamentId
                ? buildMatchManagerMatchTossPath({ ...matchManagerScopeParams, matchId })
                : '',
            });

            return (
              <div key={match.localId} className="card border-0 shadow-sm common-entity-card overflow-hidden">
                <div className="card-header bg-white border-0 py-3">
                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2">
                    <div>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="badge text-bg-dark">Match {index + 1}</span>
                        {!match.id && <span className="badge text-bg-light border">Draft</span>}
                        {isConfigured && <span className="badge text-bg-success">Configured</span>}
                        <span className="badge text-bg-info">
                          Squad: {team1Squad.length}/{squadLimit || '∞'}
                        </span>
                        <span className="badge text-bg-primary-subtle text-primary-emphasis">
                          Playing: {team1Playing.length}/{playersLimit || '∞'}
                        </span>
                      </div>

                      <div className="fw-semibold mt-1">{titleLine}</div>
                      <div className="text-muted small">{subLine}</div>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setExpandedMatches((p) => ({ ...p, [match.localId]: !p[match.localId] }))}
                      >
                        {isExpanded ? 'Collapse' : 'Expand'}
                      </button>

                      {startResumePath ? (
                        <Link className="btn btn-sm btn-outline-primary" to={startResumePath}>
                          {startResumeLabel}
                        </Link>
                      ) : (
                        <button type="button" className="btn btn-sm btn-outline-primary" disabled>
                          {startResumeLabel}
                        </button>
                      )}

                      {matchId && matchBasePath && (
                        <Link className="btn btn-sm btn-outline-success" to={`${matchBasePath}/${matchId}/scoreboard`}>
                          View scorecard
                        </Link>
                      )}

                      {matches.length > 1 && (
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveMatch(index)}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <hr className="my-3" />

                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div className="small text-muted">Public view link</div>

                    {publicViewUrl ? (
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <input
                          className="form-control form-control-sm"
                          style={{ minWidth: 280 }}
                          value={publicViewUrl}
                          readOnly
                        />
                        <a className="btn btn-sm btn-outline-secondary" href={publicViewUrl} target="_blank" rel="noreferrer">
                          Open
                        </a>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleCopyPublicView(match.id, publicViewUrl)}
                        >
                          Copy
                        </button>
                      </div>
                    ) : (
                      <div className="small text-muted">Save schedule to generate public link.</div>
                    )}
                  </div>

                  {matchCopyStatus ? <div className="text-success small mt-1">{matchCopyStatus}</div> : null}

                  <div className="mt-3">
                    <ul className="nav nav-tabs">
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link ${tab === 'basics' ? 'active' : ''}`}
                          onClick={() => setTab(match.localId, 'basics')}
                        >
                          Basics
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link ${tab === 'teams' ? 'active' : ''}`}
                          onClick={() => setTab(match.localId, 'teams')}
                        >
                          Teams
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link ${tab === 'options' ? 'active' : ''}`}
                          onClick={() => setTab(match.localId, 'options')}
                        >
                          Options
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link ${tab === 'streaming' ? 'active' : ''}`}
                          onClick={() => setTab(match.localId, 'streaming')}
                        >
                          Streaming
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>

                {isExpanded ? (
                  <div className="card-body">
                    {tab === 'basics' && (
                      <div className="row g-3">
                        <div className="col-12">
                          <SectionTitle title="Match details" subtitle="Set day, timing, and fixture details." />
                        </div>

                        <div className="col-12 col-md-3">
                          <label className="form-label">Day</label>
                          <input
                            type="number"
                            className="form-control"
                            value={match.day}
                            min={1}
                            onChange={(event) => handleMatchChange(index, 'day', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-3">
                          <label className="form-label">Match number</label>
                          <input
                            type="number"
                            className="form-control"
                            value={match.matchNumber}
                            min={1}
                            onChange={(event) => handleMatchChange(index, 'matchNumber', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-6">
                          <label className="form-label">Day label</label>
                          <input
                            type="text"
                            className="form-control"
                            value={match.dayLabel}
                            placeholder="Day 1"
                            onChange={(event) => handleMatchChange(index, 'dayLabel', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={match.date}
                            placeholder="YYYY-MM-DD"
                            onChange={(event) => handleMatchChange(index, 'date', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Time</label>
                          <input
                            type="time"
                            className="form-control"
                            value={match.time}
                            placeholder="HH:mm"
                            onChange={(event) => handleMatchChange(index, 'time', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Venue</label>
                          <input
                            type="text"
                            className="form-control"
                            value={match.configuration.venue}
                            onChange={(event) => handleConfigChange(index, 'venue', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-6">
                          <label className="form-label">Team 1</label>
                          <input
                            type="text"
                            className="form-control"
                            value={match.team1}
                            onChange={(event) => handleMatchChange(index, 'team1', event.target.value)}
                          />
                          {match.configuration.team1Captain ? (
                            <div className="text-muted small mt-1">
                              Captain: <strong>{findPlayerName(match.configuration.team1Captain)}</strong>
                            </div>
                          ) : null}
                          <div className="d-flex flex-column gap-2 mt-2">
                            <input
                              type="file"
                              accept="image/*"
                              className="form-control"
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                event.target.value = '';
                                if (file) handleTeamLogoUpload(index, 'team1', file);
                              }}
                              disabled={getLogoUploadState(match.localId, 'team1').isUploading}
                            />
                            {match.team1Logo ? (
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  src={resolveLogoUrl(match.team1Logo)}
                                  alt={match.team1 || 'Team 1'}
                                  className="rounded border"
                                  style={{ width: 44, height: 44, objectFit: 'cover' }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleMatchChange(index, 'team1Logo', '')}
                                >
                                  Remove logo
                                </button>
                              </div>
                            ) : null}
                            {getLogoUploadState(match.localId, 'team1').isUploading ? (
                              <small className="text-muted">Uploading logo…</small>
                            ) : null}
                            {getLogoUploadState(match.localId, 'team1').error ? (
                              <small className="text-danger">{getLogoUploadState(match.localId, 'team1').error}</small>
                            ) : null}
                            <div className="border rounded p-2">
                              <small className="text-muted d-block mb-1">Player join link</small>
                              <label className="form-label form-label-sm">Joined player status</label>
                              <select
                                className="form-select form-select-sm mb-2"
                                value={getSelectedJoinStatus(match, 'team1')}
                                onChange={(event) => {
                                  const joinKey = getJoinKey(match.id || match.localId, 'team1');
                                  setJoinStatusByMatchTeam((prev) => ({ ...prev, [joinKey]: event.target.value }));
                                }}
                              >
                                <option value="playing">Playing</option>
                                <option value="substitute">Substitute</option>
                                <option value="unannounced">Unannounced</option>
                              </select>
                              <div className="d-flex gap-2 mb-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  disabled={!match.id || isGeneratingJoinLinkByMatchTeam[getJoinKey(match.id || match.localId, 'team1')]}
                                  onClick={() => handleGenerateTeamJoinLink(match, 'team1')}
                                >
                                  {isGeneratingJoinLinkByMatchTeam[getJoinKey(match.id || match.localId, 'team1')] ? 'Generating…' : 'Generate link'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  disabled={!match.id}
                                  onClick={() => handleCopyJoinLink(match, 'team1')}
                                >
                                  Copy link
                                </button>
                              </div>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                readOnly
                                value={joinLinksByMatchTeam[getJoinKey(match.id || match.localId, 'team1')] ? `${joinLinksByMatchTeam[getJoinKey(match.id || match.localId, 'team1')]} (${getJoinStatusLabel(getSelectedJoinStatus(match, 'team1'))})` : ''}
                                placeholder="Generate link to view"
                              />
                              {!match.id ? <small className="text-muted">Save match first to generate join link.</small> : null}
                              {joinLinkStatusByMatchTeam[getJoinKey(match.id || match.localId, 'team1')] ? (
                                <small className="text-muted d-block mt-1">{joinLinkStatusByMatchTeam[getJoinKey(match.id || match.localId, 'team1')]}</small>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <label className="form-label">Team 2</label>
                          <input
                            type="text"
                            className="form-control"
                            value={match.team2}
                            onChange={(event) => handleMatchChange(index, 'team2', event.target.value)}
                          />
                          {match.configuration.team2Captain ? (
                            <div className="text-muted small mt-1">
                              Captain: <strong>{findPlayerName(match.configuration.team2Captain)}</strong>
                            </div>
                          ) : null}
                          <div className="d-flex flex-column gap-2 mt-2">
                            <input
                              type="file"
                              accept="image/*"
                              className="form-control"
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                event.target.value = '';
                                if (file) handleTeamLogoUpload(index, 'team2', file);
                              }}
                              disabled={getLogoUploadState(match.localId, 'team2').isUploading}
                            />
                            {match.team2Logo ? (
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  src={resolveLogoUrl(match.team2Logo)}
                                  alt={match.team2 || 'Team 2'}
                                  className="rounded border"
                                  style={{ width: 44, height: 44, objectFit: 'cover' }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleMatchChange(index, 'team2Logo', '')}
                                >
                                  Remove logo
                                </button>
                              </div>
                            ) : null}
                            {getLogoUploadState(match.localId, 'team2').isUploading ? (
                              <small className="text-muted">Uploading logo…</small>
                            ) : null}
                            {getLogoUploadState(match.localId, 'team2').error ? (
                              <small className="text-danger">{getLogoUploadState(match.localId, 'team2').error}</small>
                            ) : null}
                            <div className="border rounded p-2">
                              <small className="text-muted d-block mb-1">Player join link</small>
                              <label className="form-label form-label-sm">Joined player status</label>
                              <select
                                className="form-select form-select-sm mb-2"
                                value={getSelectedJoinStatus(match, 'team2')}
                                onChange={(event) => {
                                  const joinKey = getJoinKey(match.id || match.localId, 'team2');
                                  setJoinStatusByMatchTeam((prev) => ({ ...prev, [joinKey]: event.target.value }));
                                }}
                              >
                                <option value="playing">Playing</option>
                                <option value="substitute">Substitute</option>
                                <option value="unannounced">Unannounced</option>
                              </select>
                              <div className="d-flex gap-2 mb-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  disabled={!match.id || isGeneratingJoinLinkByMatchTeam[getJoinKey(match.id || match.localId, 'team2')]}
                                  onClick={() => handleGenerateTeamJoinLink(match, 'team2')}
                                >
                                  {isGeneratingJoinLinkByMatchTeam[getJoinKey(match.id || match.localId, 'team2')] ? 'Generating…' : 'Generate link'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  disabled={!match.id}
                                  onClick={() => handleCopyJoinLink(match, 'team2')}
                                >
                                  Copy link
                                </button>
                              </div>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                readOnly
                                value={joinLinksByMatchTeam[getJoinKey(match.id || match.localId, 'team2')] ? `${joinLinksByMatchTeam[getJoinKey(match.id || match.localId, 'team2')]} (${getJoinStatusLabel(getSelectedJoinStatus(match, 'team2'))})` : ''}
                                placeholder="Generate link to view"
                              />
                              {!match.id ? <small className="text-muted">Save match first to generate join link.</small> : null}
                              {joinLinkStatusByMatchTeam[getJoinKey(match.id || match.localId, 'team2')] ? (
                                <small className="text-muted d-block mt-1">{joinLinkStatusByMatchTeam[getJoinKey(match.id || match.localId, 'team2')]}</small>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {tab === 'teams' && (
                      <div className="row g-4">
                        <div className="col-12">
                          <SectionTitle
                            title="Team selection"
                            subtitle="Select squad players, then assign each player as Playing, Substitute, or Unannounced."
                          />
                        </div>

                        <div className="col-12">
                          <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body">
                              <div className="row align-items-end g-3">
                                <div className="col-12 col-md-4 col-lg-3">
                                  <label className="form-label fw-semibold mb-1">Playing Players Per Side</label>
                                  <input
                                    type="number"
                                    min="1"
                                    className="form-control form-control-lg rounded-3"
                                    value={match.configuration.playingCount ?? match.configuration.playersPerSide}
                                    onChange={(event) => handleConfigChange(index, 'playingCount', event.target.value)}
                                  />
                                  <div className="form-text mt-1">
                                    {playersLimit ? `Playing required: ${playersLimit} players per side` : 'No playing XI limit applied.'}
                                  </div>
                                </div>

                                <div className="col-12 col-md-4 col-lg-3">
                                  <label className="form-label fw-semibold mb-1">Squad Size</label>
                                  <input
                                    type="number"
                                    min={playersLimit || 1}
                                    className="form-control form-control-lg rounded-3"
                                    value={match.configuration.squadSize}
                                    readOnly
                                  />
                                  <div className="form-text mt-1">
                                    Squad size is derived from selected players; Players per side is the active-player limit.
                                  </div>
                                </div>

                                <div className="col-12 col-md-8 col-lg-9">
                                  <div className="d-flex flex-wrap gap-2 justify-content-md-end">
              <span className="badge text-bg-light border px-3 py-2 rounded-pill">
                {match.team1 || 'Team 1'} Squad: {team1Squad.length}

              </span>
                                    <span className="badge text-bg-light border px-3 py-2 rounded-pill">
                {match.team2 || 'Team 2'} Squad: {team2Squad.length}

              </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Team 1 */}
                        <div className="col-12 col-xl-6">
                          <div className="card border-0 shadow-sm rounded-4 h-100">
                            <div className="card-header bg-white border-0 pt-4 px-4">
                              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                  <h3 className="h5 mb-1 fw-semibold">{match.team1 || 'Team 1'}</h3>
                                  <p className="text-muted small mb-0">New players receive a default status; selected player statuses remain editable.</p>
                                </div>
                                <span className="badge text-bg-primary px-3 py-2 rounded-pill">
            {team1Squad.length}
                                   in squad
          </span>
                              </div>
                            </div>

                            <div className="card-body px-4 pb-4">
                              <div className="row g-2 mb-3">
                                <div className="col-12 col-md">
                                  <input
                                    className="form-control rounded-3"
                                    placeholder="Search players..."
                                    value={team1Search}
                                    onChange={(event) => setSearchValue(match.localId, 'team1Players', event.target.value)}
                                  />
                                </div>
                                <div className="col-6 col-md-auto">
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary w-100 rounded-3"
                                    onClick={() => {
                                      const ids = (team1Search ? filteredTeam1 : team1Options).map((p) => p.id);
                                      setPlayersForTeam(index, 'team1SquadPlayers', 'team1PlayingPlayers', ids);
                                    }}
                                  >
                                    Select squad
                                  </button>
                                </div>
                                <div className="col-6 col-md-auto">
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary w-100 rounded-3"
                                    onClick={() => setPlayersForTeam(index, 'team1SquadPlayers', 'team1PlayingPlayers', [])}
                                  >
                                    Clear
                                  </button>
                                </div>
                              </div>

                              <div className="mb-3">
                                {team1Squad.length ? (
                                  <div className="d-flex flex-wrap gap-2">
                                    {team1Squad.map((id) => (
                                      <span
                                        key={`chip-t1-${match.localId}-${id}`}
                                        className="badge text-bg-light border px-3 py-2 rounded-pill"
                                      >
                  {findPlayerName(id)}
                </span>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-muted small">No players selected yet.</div>
                                )}
                              </div>

                              <div
                                className="border rounded-4 p-3 bg-light-subtle"
                                style={{ maxHeight: 340, overflow: 'auto' }}
                              >
                                <div className="d-grid gap-2">
                                  {filteredTeam1.map((player) => {
                                    const isSelected = team1Squad.includes(player.id);
                                    const isDisabled = false;
                                    const isAutoPlaying = team1Playing.includes(player.id);
                                    const isSubstitute = team1Substitutes.includes(player.id);
                                    const playerStatus = isAutoPlaying ? 'playing' : (isSubstitute ? 'substitute' : 'unannounced');

                                    return (
                                      <label
                                        key={`team1-${match.localId}-${player.id}`}
                                        className={`d-flex align-items-center justify-content-between gap-3 p-3 rounded-3 border mb-0 manual-player-option${isDisabled ? ' disabled-player' : ' bg-white'}`}
                                        title={undefined}
                                      >
                                        <div className="d-flex align-items-center gap-3">
                                          <input
                                            className="form-check-input mt-0"
                                            type="checkbox"
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={() => toggleSquadPlayer(index, 'team1SquadPlayers', 'team1PlayingPlayers', player.id)}
                                          />
                                          <div>
                                            <div className="fw-medium">{player.name}</div>
                                            {player.role ? <div className="text-muted small">{player.role}</div> : null}

                                          </div>
                                        </div>

                                        {isSelected ? (
                                          <select
                                            className="form-select form-select-sm rounded-3"
                                            style={{ maxWidth: 160 }}
                                            value={playerStatus}
                                            onClick={(event) => event.stopPropagation()}
                                            onChange={(event) => changePlayerStatus(index, 'team1SquadPlayers', 'team1PlayingPlayers', player.id, event.target.value)}
                                            aria-label={`${player.name} status`}
                                          >
                                            <option value="playing">Playing</option>
                                            <option value="substitute">Substitute</option>
                                            <option value="unannounced">Unannounced</option>
                                          </select>
                                        ) : null}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>

                              <div className="mt-4 p-3 rounded-4 border bg-white">
                                <h4 className="h6 fw-semibold mb-3">Assign roles</h4>
                                <div className="row g-3">
                                  <div className="col-12">
                                    <label className="form-label small fw-semibold">Captain</label>
                                    <select
                                      className="form-select rounded-3"
                                      value={match.configuration.team1Captain}
                                      onChange={(event) => handleConfigChange(index, 'team1Captain', event.target.value)}
                                    >
                                      <option value="">Select captain</option>
                                      {team1AnnouncedOptions.map((player) => (
                                        <option key={`team1-captain-${player.id}`} value={player.id}>
                                          {player.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="col-12">
                                    <label className="form-label small fw-semibold">Vice captain</label>
                                    <select
                                      className="form-select rounded-3"
                                      value={match.configuration.team1ViceCaptain}
                                      onChange={(event) => handleConfigChange(index, 'team1ViceCaptain', event.target.value)}
                                    >
                                      <option value="">Select vice captain</option>
                                      {team1AnnouncedOptions.map((player) => (
                                        <option key={`team1-vice-${player.id}`} value={player.id}>
                                          {player.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="col-12">
                                    <label className="form-label small fw-semibold">Wicket keeper</label>
                                    <select
                                      className="form-select rounded-3"
                                      value={match.configuration.team1WicketKeeper}
                                      onChange={(event) => handleConfigChange(index, 'team1WicketKeeper', event.target.value)}
                                    >
                                      <option value="">Select wicket keeper</option>
                                      {team1AnnouncedOptions.map((player) => (
                                        <option key={`team1-keeper-${player.id}`} value={player.id}>
                                          {player.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Team 2 */}
                        <div className="col-12 col-xl-6">
                          <div className="card border-0 shadow-sm rounded-4 h-100">
                            <div className="card-header bg-white border-0 pt-4 px-4">
                              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                  <h3 className="h5 mb-1 fw-semibold">{match.team2 || 'Team 2'}</h3>
                                  <p className="text-muted small mb-0">New players receive a default status; selected player statuses remain editable.</p>
                                </div>
                                <span className="badge text-bg-success px-3 py-2 rounded-pill">
            {team2Squad.length}
                                   in squad
          </span>
                              </div>
                            </div>

                            <div className="card-body px-4 pb-4">
                              <div className="row g-2 mb-3">
                                <div className="col-12 col-md">
                                  <input
                                    className="form-control rounded-3"
                                    placeholder="Search players..."
                                    value={team2Search}
                                    onChange={(event) => setSearchValue(match.localId, 'team2Players', event.target.value)}
                                  />
                                </div>
                                <div className="col-6 col-md-auto">
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary w-100 rounded-3"
                                    onClick={() => {
                                      const ids = (team2Search ? filteredTeam2 : team2Options).map((p) => p.id);
                                      setPlayersForTeam(index, 'team2SquadPlayers', 'team2PlayingPlayers', ids);
                                    }}
                                  >
                                    Select squad
                                  </button>
                                </div>
                                <div className="col-6 col-md-auto">
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary w-100 rounded-3"
                                    onClick={() => setPlayersForTeam(index, 'team2SquadPlayers', 'team2PlayingPlayers', [])}
                                  >
                                    Clear
                                  </button>
                                </div>
                              </div>

                              <div className="mb-3">
                                {team2Squad.length ? (
                                  <div className="d-flex flex-wrap gap-2">
                                    {team2Squad.map((id) => (
                                      <span
                                        key={`chip-t2-${match.localId}-${id}`}
                                        className="badge text-bg-light border px-3 py-2 rounded-pill"
                                      >
                  {findPlayerName(id)}
                </span>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-muted small">No players selected yet.</div>
                                )}
                              </div>

                              <div
                                className="border rounded-4 p-3 bg-light-subtle"
                                style={{ maxHeight: 340, overflow: 'auto' }}
                              >
                                <div className="d-grid gap-2">
                                  {filteredTeam2.map((player) => {
                                    const isSelected = team2Squad.includes(player.id);
                                    const isDisabled = false;
                                    const isAutoPlaying = team2Playing.includes(player.id);
                                    const isSubstitute = team2Substitutes.includes(player.id);
                                    const playerStatus = isAutoPlaying ? 'playing' : (isSubstitute ? 'substitute' : 'unannounced');

                                    return (
                                      <label
                                        key={`team2-${match.localId}-${player.id}`}
                                        className={`d-flex align-items-center justify-content-between gap-3 p-3 rounded-3 border mb-0 manual-player-option${isDisabled ? ' disabled-player' : ' bg-white'}`}
                                        title={undefined}
                                      >
                                        <div className="d-flex align-items-center gap-3">
                                          <input
                                            className="form-check-input mt-0"
                                            type="checkbox"
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={() => toggleSquadPlayer(index, 'team2SquadPlayers', 'team2PlayingPlayers', player.id)}
                                          />
                                          <div>
                                            <div className="fw-medium">{player.name}</div>
                                            {player.role ? <div className="text-muted small">{player.role}</div> : null}

                                          </div>
                                        </div>

                                        {isSelected ? (
                                          <select
                                            className="form-select form-select-sm rounded-3"
                                            style={{ maxWidth: 160 }}
                                            value={playerStatus}
                                            onClick={(event) => event.stopPropagation()}
                                            onChange={(event) => changePlayerStatus(index, 'team2SquadPlayers', 'team2PlayingPlayers', player.id, event.target.value)}
                                            aria-label={`${player.name} status`}
                                          >
                                            <option value="playing">Playing</option>
                                            <option value="substitute">Substitute</option>
                                            <option value="unannounced">Unannounced</option>
                                          </select>
                                        ) : null}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>

                              <div className="mt-4 p-3 rounded-4 border bg-white">
                                <h4 className="h6 fw-semibold mb-3">Assign roles</h4>
                                <div className="row g-3">
                                  <div className="col-12">
                                    <label className="form-label small fw-semibold">Captain</label>
                                    <select
                                      className="form-select rounded-3"
                                      value={match.configuration.team2Captain}
                                      onChange={(event) => handleConfigChange(index, 'team2Captain', event.target.value)}
                                    >
                                      <option value="">Select captain</option>
                                      {team2AnnouncedOptions.map((player) => (
                                        <option key={`team2-captain-${player.id}`} value={player.id}>
                                          {player.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="col-12">
                                    <label className="form-label small fw-semibold">Vice captain</label>
                                    <select
                                      className="form-select rounded-3"
                                      value={match.configuration.team2ViceCaptain}
                                      onChange={(event) => handleConfigChange(index, 'team2ViceCaptain', event.target.value)}
                                    >
                                      <option value="">Select vice captain</option>
                                      {team2AnnouncedOptions.map((player) => (
                                        <option key={`team2-vice-${player.id}`} value={player.id}>
                                          {player.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="col-12">
                                    <label className="form-label small fw-semibold">Wicket keeper</label>
                                    <select
                                      className="form-select rounded-3"
                                      value={match.configuration.team2WicketKeeper}
                                      onChange={(event) => handleConfigChange(index, 'team2WicketKeeper', event.target.value)}
                                    >
                                      <option value="">Select wicket keeper</option>
                                      {team2AnnouncedOptions.map((player) => (
                                        <option key={`team2-keeper-${player.id}`} value={player.id}>
                                          {player.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {tab === 'options' && (
                      <div className="row g-3">
                        <div className="col-12">
                          <SectionTitle title="Match options" subtitle="Officials, notes, and match rules." />
                        </div>

                        <div className="col-12 col-md-6">
                          <label className="form-label">Notes</label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={match.configuration.notes}
                            onChange={(event) => handleConfigChange(index, 'notes', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-6">
                          <label className="form-label">Scorer</label>
                          <input
                            type="text"
                            className="form-control"
                            value={match.configuration.scorer}
                            onChange={(event) => handleConfigChange(index, 'scorer', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Umpire 1</label>
                          <input
                            type="text"
                            className="form-control"
                            value={match.configuration.umpire1}
                            onChange={(event) => handleConfigChange(index, 'umpire1', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Umpire 2</label>
                          <input
                            type="text"
                            className="form-control"
                            value={match.configuration.umpire2}
                            onChange={(event) => handleConfigChange(index, 'umpire2', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Match referee</label>
                          <input
                            type="text"
                            className="form-control"
                            value={match.configuration.matchReferee}
                            onChange={(event) => handleConfigChange(index, 'matchReferee', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Overs</label>
                          <input
                            type="number"
                            className="form-control"
                            value={match.configuration.overs}
                            onChange={(event) => handleConfigChange(index, 'overs', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Max overs per player</label>
                          <input
                            type="number"
                            className="form-control"
                            value={match.configuration.maxOversPerPlayer}
                            onChange={(event) => handleConfigChange(index, 'maxOversPerPlayer', event.target.value)}
                          />
                          <div className="form-text">Optional. Leave blank for no per-bowler over limit.</div>
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Max players allowed to bowl full quota</label>
                          <input
                            type="number"
                            className="form-control"
                            value={match.configuration.maxPlayersAtMaxOvers}
                            onChange={(event) => handleConfigChange(index, 'maxPlayersAtMaxOvers', event.target.value)}
                          />
                          <div className="form-text">Example: if max overs per player is 4 and this value is 3, only 3 bowlers can bowl all 4 overs.</div>
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Power play start over</label>
                          <input
                            type="number"
                            className="form-control"
                            value={match.configuration.powerPlayStartOver}
                            onChange={(event) => handleConfigChange(index, 'powerPlayStartOver', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label">Power play end over</label>
                          <input
                            type="number"
                            className="form-control"
                            value={match.configuration.powerPlayEndOver}
                            onChange={(event) => handleConfigChange(index, 'powerPlayEndOver', event.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-4 d-flex align-items-end">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              id={`allow-last-batsman-${match.localId}`}
                              type="checkbox"
                              checked={Boolean(match.configuration.allowLastBatsman)}
                              onChange={(event) => handleConfigChange(index, 'allowLastBatsman', event.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={`allow-last-batsman-${match.localId}`}>
                              Allow last batsman to continue
                            </label>
                            <div className="form-text">Final batter can keep batting after all wickets fall.</div>
                          </div>
                        </div>

                      </div>
                    )}

                    {tab === 'streaming' && (
                      <div className="row g-3">
                        <div className="col-12">
                          <SectionTitle title="Live streaming" subtitle="Provide the watch link and stream credentials for this match broadcast." />
                        </div>

                        {(() => {
                          const streamingMode = normalizeStreamingMode(match.configuration.streamingConfig?.mode || match.configuration.streamingMode);
                          const isObsMode = streamingMode === STREAMING_MODES.OBS;
                          const isLocalMode = streamingMode === STREAMING_MODES.LOCAL_MAC_TESTING;
                          const isDirectMode = streamingMode === STREAMING_MODES.DIRECT_MOBILE;
                          const selectedStreamProvider = normalizeStreamProvider(match.configuration.streamProvider);
                          const isYouTubeProvider = selectedStreamProvider === STREAM_PROVIDERS.YOUTUBE;
                          const obsCameras = match.configuration.streamingConfig?.obsConfig?.cameras || [];
                          const streamDetails = streamDetailsByMatch[match.localId] || null;
                          const liveStatus = liveStatusByMatch[match.localId] || null;
                          const relayStatus = relayStatusByMatch[match.localId] || null;
                          const livePlaybackUrl =
                            match.configuration.youtubeLiveLink ||
                            match.configuration.streamingConfig?.youtube?.playbackUrl ||
                            liveStatus?.playbackUrl ||
                            streamDetails?.youtube?.playbackUrl ||
                            '';
                          const obsYouTubeIngestUrl = streamDetails?.youtube?.ingestUrl || match.configuration.streamingConfig?.youtube?.ingestUrl || '';
                          const obsYouTubeStreamKey = streamDetails?.youtube?.streamKey || liveStatus?.streamKey || match.configuration.streamingConfig?.youtube?.streamKey || '';
                          const liveHealthKey = resolveLiveHealth(liveStatus);
                          const liveHealthBadge = LIVE_HEALTH_BADGE[liveHealthKey] || LIVE_HEALTH_BADGE.offline;
                          const isGeneratingYouTube = Boolean(isGeneratingYouTubeByMatch[match.localId]);

                          return (
                            <>
                              <div className="col-12">
                                <div className="card shadow-sm border-0 mb-0">
                                  <div className="card-body d-flex flex-column gap-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <h2 className="h6 mb-0">Live stream status</h2>
                                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => refreshLiveStatus(match)} disabled={!match.id}>Refresh</button>
                                    </div>
                                    <div><small className="text-muted">Streaming mode</small><br /><strong>{liveStatus?.streamingMode || streamDetails?.streamingMode || '—'}</strong></div>
                                    <div><small className="text-muted">Provider</small><br /><strong>{liveStatus?.provider || streamDetails?.streamProvider || '—'}</strong></div>
                                    <div><small className="text-muted">Live status</small><br /><span className={`badge ${liveHealthBadge.className}`}>{liveHealthBadge.label}</span></div>
                                    <div><small className="text-muted">YouTube ingest status</small><br /><strong>{liveStatus?.streamStatus || 'unknown'}</strong></div>
                                    <div><small className="text-muted">YouTube broadcast status</small><br /><strong>{liveStatus?.youtubeStreamStatus || 'unknown'}</strong></div>
                                    {typeof liveStatus?.concurrentViewers === 'number' ? <div><small className="text-muted">Concurrent viewers</small><br /><strong>{liveStatus.concurrentViewers}</strong></div> : null}
                                    <div><small className="text-muted">Playback URL</small><br />{livePlaybackUrl ? <code>{livePlaybackUrl}</code> : <span className="text-muted">Not available yet</span>}</div>
                                    {livePlaybackUrl ? <div><a className="btn btn-sm btn-outline-primary" href={livePlaybackUrl} target="_blank" rel="noreferrer">Watch live</a></div> : null}
                                    {liveStatusErrorByMatch[match.localId] ? <p className="text-danger small mb-0">{liveStatusErrorByMatch[match.localId]}</p> : null}
                                  </div>
                                </div>
                              </div>

                              <div className="col-12 col-lg-6">
                                <label className="form-label">Streaming Mode</label>
                                <select className="form-select" value={streamingMode} onChange={(event) => handleStreamingChange(index, 'streamingMode', event.target.value)}>
                                  <option value={STREAMING_MODES.DIRECT_MOBILE}>{STREAMING_MODES.DIRECT_MOBILE}</option>
                                  <option value={STREAMING_MODES.OBS}>{STREAMING_MODES.OBS}</option>
                                  {isLocalDev ? <option value={STREAMING_MODES.LOCAL_MAC_TESTING}>{STREAMING_MODES.LOCAL_MAC_TESTING}</option> : null}
                                </select>
                              </div>

                              {isDirectMode ? (
                                <div className="col-12 col-lg-6">
                                  <label className="form-label">Stream Provider</label>
                                  <select className="form-select" value={selectedStreamProvider} onChange={(event) => handleStreamingChange(index, 'streamProvider', event.target.value)}>
                                    <option value={STREAM_PROVIDERS.YOUTUBE}>{STREAM_PROVIDERS.YOUTUBE}</option>
                                    <option value={STREAM_PROVIDERS.FACEBOOK}>{STREAM_PROVIDERS.FACEBOOK}</option>
                                  </select>
                                </div>
                              ) : null}

                              {isDirectMode && isYouTubeProvider ? (
                                <div className="col-12 col-lg-6">
                                  <label className="form-label">YouTube Live Link</label>
                                  <input
                                    type="url"
                                    className="form-control"
                                    value={match.configuration.youtubeLiveLink || ''}
                                    onChange={(event) => handleStreamingChange(index, 'youtubeLiveLink', event.target.value)}
                                    placeholder="Will appear after Generate YouTube"
                                  />
                                  <div className="d-flex flex-wrap gap-2 mt-2">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={() => handleGeneratePublicStreamUrl(match, index)}
                                      disabled={isGeneratingYouTube || !match.id}
                                    >
                                      Generate YouTube
                                      {isGeneratingYouTube ? (<span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />) : null}
                                    </button>
                                  </div>
                                  {match.configuration.youtubeLiveLink ? (
                                    <div className="mt-2 d-flex flex-column gap-1">
                                      <div className="d-flex align-items-center gap-2">
                                        <a href={match.configuration.youtubeLiveLink} target="_blank" rel="noreferrer" className="small">Open Live Link</a>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyText(match.configuration.youtubeLiveLink, 'Copied YouTube live link.')}>Copy</button>
                                      </div>
                                      <small className="text-muted text-break">{match.configuration.youtubeLiveLink}</small>
                                    </div>
                                  ) : (
                                    <small className="text-muted d-block mt-2">Will appear after Generate YouTube</small>
                                  )}
                                  {streamUrlStatusByMatch[match.localId] ? <p className="text-muted small mt-2 mb-0">{streamUrlStatusByMatch[match.localId]}</p> : null}
                                </div>
                              ) : null}

                              {(isObsMode || isLocalMode) ? (
                                <div className="col-12 col-lg-6">
                                  <label className="form-label">Playback URL</label>
                                  <input
                                    type="url"
                                    className="form-control"
                                    value={match.configuration.streamingConfig?.obs?.playbackUrl || match.configuration.livePlaybackUrl || ''}
                                    onChange={(event) => handleStreamingChange(index, 'livePlaybackUrl', event.target.value)}
                                    placeholder="https://example.com/watch"
                                  />
                                  <small className="text-muted d-block mt-1">Optional public playback/watch URL</small>
                                </div>
                              ) : null}

                              {!isDirectMode ? (
                                <>
                                  <div className="col-12 col-lg-6">
                                    <label className="form-label">RTMP ingest URL</label>
                                    <input
                                      type="url"
                                      className="form-control"
                                      value={match.configuration.streamingConfig?.obs?.ingestUrl || ''}
                                      onChange={(event) => handleStreamingChange(index, 'rtmpIngestUrl', event.target.value)}
                                      placeholder="rtmp://<server>/live"
                                    />
                                    <small className="text-muted d-block mt-1">Common ingest URL for all camera feeds</small>
                                  </div>
                                  <div className="col-12 col-lg-6">
                                    <label className="form-label">RTMP stream key</label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={match.configuration.streamingConfig?.obs?.streamKey || match.configuration.rtmpStreamKey || ''}
                                      onChange={(event) => handleStreamingChange(index, 'rtmpStreamKey', event.target.value)}
                                      placeholder="RTMP stream key"
                                    />
                                  </div>
                                </>
                              ) : null}

                              {isObsMode ? (
                                <>
                                  <div className="col-12 col-lg-4">
                                    <label className="form-label">OBS camera feeds</label>
                                    <input
                                      type="number"
                                      min="1"
                                      max="8"
                                      className="form-control"
                                      value={match.configuration.streamingConfig?.obsConfig?.cameraCount || DEFAULT_OBS_CAMERA_COUNT}
                                      onChange={(event) => handleStreamingChange(index, 'obsCameraCount', event.target.value)}
                                    />
                                    <small className="text-muted d-block mt-1">Unique stream keys will be generated per camera feed.</small>
                                  </div>
                                  <div className="col-12">
                                    <div className="border rounded p-3">
                                      <h6 className="mb-2">OBS Camera Feeds</h6>
                                      <div className="small text-muted mb-1">Use these feeds as separate inputs in OBS.</div>
                                      <div className="small text-muted mb-2">Each mobile device should stream to one assigned camera feed.</div>
                                      <div className="d-grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                                      {obsCameras.map((camera, cameraIndex) => (
                                        <div key={`obs-camera-${cameraIndex}`} className="border rounded p-2">
                                          <div className="fw-semibold mb-1">{camera.label || `Camera ${cameraIndex + 1}`}</div>
                                          <div className="mb-1"><small className="text-muted">Camera ID</small><br /><code>{camera.id || `cam${cameraIndex + 1}`}</code></div>
                                          <div className="mb-1"><small className="text-muted">Ingest URL</small><br /><code>{camera.ingestUrl || '—'}</code></div>
                                          <div className="mb-1"><small className="text-muted">Stream Key</small><br /><code>{camera.streamKey || '—'}</code></div>
                                          <div><small className="text-muted">Publish URL</small><br /><code>{camera.publishUrl || (camera.ingestUrl && camera.streamKey ? `${camera.ingestUrl}/${camera.streamKey}` : '—')}</code></div>
                                          <div className="d-flex gap-2 mt-2">
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyText(camera.ingestUrl, 'Copied ingest URL.')} disabled={!camera.ingestUrl}>Copy URL</button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyText(camera.streamKey, 'Copied stream key.')}>Copy key</button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyText(camera.publishUrl || (camera.ingestUrl && camera.streamKey ? `${camera.ingestUrl}/${camera.streamKey}` : ''), 'Copied publish URL.')}>Copy publish URL</button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    </div>
                                  </div>
                                </>
                              ) : null}

                              {isObsMode ? (
                                <div className="col-12">
                                  <div className="border rounded p-3 bg-light">
                                    <h6 className="mb-2">OBS Settings</h6>
                                    <div><small className="text-muted">OBS ingest URL</small><br /><code>{streamDetails?.obs?.ingestUrl || match.configuration.streamingConfig?.obs?.ingestUrl || '—'}</code></div>
                                    <div><small className="text-muted">Primary OBS key</small><br /><code>{streamDetails?.obs?.streamKey || match.configuration.streamingConfig?.obsConfig?.cameras?.[0]?.streamKey || match.configuration.streamingConfig?.obs?.streamKey || '—'}</code></div>
                                    <hr className="my-2" />
                                    <div><small className="text-muted">YouTube ingest URL</small><br /><code>{obsYouTubeIngestUrl || '—'}</code></div>
                                    <div><small className="text-muted">YouTube stream key</small><br /><code>{obsYouTubeStreamKey || '—'}</code></div>
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleGeneratePublicStreamUrl(match, index)} disabled={isGeneratingYouTube || !match.id}>
                                        Generate YouTube
                                        {isGeneratingYouTube ? (<span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />) : null}
                                      </button>
                                      {livePlaybackUrl ? <a className="btn btn-sm btn-outline-primary" href={livePlaybackUrl} target="_blank" rel="noreferrer">Watch live</a> : null}
                                    </div>
                                    <div className="small text-muted mt-2">OBS can publish to this match-specific YouTube event via relay.</div>
                                    <div className="small mt-2">Relay running: <strong>{relayStatus?.running ? 'Yes' : 'No'}</strong></div>
                                    {relayStatus?.lastError ? <div className="small text-danger mt-1">Last error: {relayStatus.lastError}</div> : null}
                                    <div className="d-flex gap-2 mt-2">
                                      <button type="button" className="btn btn-sm btn-outline-success" onClick={() => handleStartRelay(match)} disabled={!match.id}>Start relay</button>
                                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleStopRelay(match)} disabled={!match.id}>Stop relay</button>
                                    </div>
                                  </div>
                                </div>
                              ) : null}

                              {isLocalMode ? (
                                <>
                                  <div className="col-12">
                                    <div className="form-check form-switch">
                                      <input className="form-check-input" type="checkbox" role="switch" id={`localPreviewToggle-${match.localId}`} checked={Boolean(localPreviewEnabledByMatch[match.localId])} onChange={() => handleToggleLocalPreview(match)} />
                                      <label className="form-check-label" htmlFor={`localPreviewToggle-${match.localId}`}>Local Preview</label>
                                    </div>
                                    {localPreviewStatusByMatch[match.localId] ? <p className="text-muted small mt-2 mb-0">{localPreviewStatusByMatch[match.localId]}</p> : null}
                                    {localPreviewEnabledByMatch[match.localId] && localPreviewConfigByMatch[match.localId] ? (
                                      <div className="border rounded p-3 mt-2 d-flex flex-column gap-2">
                                        <div><small className="text-muted d-block">RTMP URL</small><code>{localPreviewConfigByMatch[match.localId].rtmpUrl}</code><button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={() => copyText(localPreviewConfigByMatch[match.localId].rtmpUrl, 'Copied RTMP URL.')}>Copy</button></div>
                                        <div><small className="text-muted d-block">Playback URL</small><code>{localPreviewConfigByMatch[match.localId].playUrlRtmp}</code><button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={() => copyText(localPreviewConfigByMatch[match.localId].playUrlRtmp, 'Copied playback URL.')}>Copy</button></div>
                                      </div>
                                    ) : null}
                                  </div>
                                  {streamDetails?.local ? (
                                    <div className="col-12">
                                      <div className="border rounded p-3 bg-light">
                                        <h6 className="mb-2">Local Preview</h6>
                                        <div><small className="text-muted">Local RTMP ingest URL</small><br /><code>{streamDetails.local.ingestUrl}</code></div>
                                        <div><small className="text-muted">Local stream key</small><br /><code>{streamDetails.local.streamKey}</code></div>
                                        <div><small className="text-muted">ffplay command</small><br /><code>{`ffplay -fflags nobuffer -flags low_delay -framedrop ${streamDetails.local.ffplayUrl}`}</code></div>
                                        <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => copyText(`ffplay -fflags nobuffer -flags low_delay -framedrop ${streamDetails.local.ffplayUrl}`, 'Copied ffplay command.')}>Copy ffplay</button>
                                      </div>
                                    </div>
                                  ) : null}
                                  <div className="col-12 col-lg-6">
                                    <label className="form-label">Local RTMP ingest URL</label>
                                    <input type="url" className="form-control" value={match.configuration.streamingConfig?.local?.ingestUrl || ''} onChange={(event) => handleStreamingChange(index, 'rtmpIngestUrl', event.target.value)} placeholder="rtmp://<LAN_IP>:1935/live" />
                                  </div>
                                  <div className="col-12 col-lg-6">
                                    <label className="form-label">RTMP stream key</label>
                                    <input type="text" className="form-control" value={match.configuration.streamingConfig?.local?.streamKey || ''} onChange={(event) => handleStreamingChange(index, 'rtmpStreamKey', event.target.value)} placeholder="RTMP stream key" />
                                  </div>
                                </>
                              ) : null}

                              <div className="col-12 col-lg-6">
                                <label className="form-label">Overlay mode</label>
                                <select className="form-select" value={match.configuration.overlayMode || 'burn_in'} onChange={(event) => handleStreamingChange(index, 'overlayMode', event.target.value)} disabled={isObsMode}>
                                  <option value="none">none</option>
                                  <option value="burn_in">burn_in</option>
                                </select>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bottom-0 bg-white border-top py-2" style={{ zIndex: 1015 }}>
        <div className="container d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-outline-primary" onClick={handleAddMatch}>
            Add match
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MatchManagerManualFormatPage;
